import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Zap, Settings, History, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Trash2, MessageCircle, Edit3, MoreVertical, CheckSquare, User, Mail, ArrowLeft, ChevronRight } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { 
  getAutomations, 
  createAutomation, 
  toggleAutomationLive, 
  updateAutomation,
  deleteAutomation,
  getCurrentBusinessId,
  getAutomationTriggers,
  saveAutomationTriggers,
  getAutomationActions,
  saveAutomationActions,
  getAutomationConnections,
  saveWireConnections,
  createAutomationConnection,
  getAutomationExecutions,

  getExecutionSteps
} from '../lib/automationsV2';
import { getBoards } from '../lib/boards';
import { getBoardWithColumns } from '../lib/kanban';
import { emailTemplateService } from '../services/emailTemplateService';
import { AutomationV2, TriggerNode as TriggerNodeType, ActionNode as ActionNodeType, AutomationExecutionV2, AutomationActionExecutionV2 } from '../types/automationsV2';

interface WireState {
  isDragging: boolean;
  startNodeId: string | null;
  startPosition: { x: number; y: number };
  currentPosition: { x: number; y: number };
}

interface WireConnection {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  fromPort: 'outflow';
  toPort: 'inflow';
}

// Connection Port Component
interface ConnectionPortProps {
  nodeId: string;
  type: 'inflow' | 'outflow';
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onWireDropped?: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
}

const ConnectionPort: React.FC<ConnectionPortProps> = ({ nodeId, type, onStartWireDrag, onWireDropped }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only start wire drags from outflow ports
    if (type !== 'outflow') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get the canvas element to calculate relative position
    const canvasElement = e.currentTarget.closest('[data-canvas]') as HTMLElement;
    if (!canvasElement) return;
    
    const portRect = e.currentTarget.getBoundingClientRect();
    const canvasRect = canvasElement.getBoundingClientRect();
    
    // Calculate position relative to canvas
    const position = {
      x: portRect.left + portRect.width / 2 - canvasRect.left,
      y: portRect.top + portRect.height / 2 - canvasRect.top
    };
    
    console.log(`🔗 Starting wire drag from ${nodeId} (${type}) at position:`, position);
    onStartWireDrag(nodeId, position);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Only handle wire drops on inflow ports
    if (type !== 'inflow' || !onWireDropped) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`🔗 Wire dropped on ${nodeId} (${type})`);
    onWireDropped(nodeId, type);
  };

  return (
    <div
      className={`absolute w-4 h-4 rounded-full border-2 shadow-lg hover:scale-110 transition-transform ${
        type === 'inflow' 
          ? 'bg-white border-gray-500 top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer hover:bg-gray-50' 
          : 'bg-gray-500 border-white hover:bg-gray-600 bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 cursor-crosshair'
      }`}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      title={type === 'inflow' ? 'Input connection' : 'Output connection'}
      data-node-id={nodeId}
      data-port-type={type}
    />
  );
};

// Trigger Node Component
interface TriggerNodeComponentProps {
  trigger: TriggerNodeType;
  onMouseDown: (e: React.MouseEvent, triggerId: string) => void;
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onSettingsClick: (triggerId: string) => void;
  onWireDropped: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
}

const TriggerNode: React.FC<TriggerNodeComponentProps> = ({ 
  trigger, 
  onMouseDown, 
  onStartWireDrag, 
  onSettingsClick,
  onWireDropped
}) => {
  const formatEventType = (eventType: string, triggerType?: string) => {
    if (triggerType === 'form_submission') {
      return 'Form Submitted';
    }
    return eventType.charAt(0).toUpperCase() + eventType.slice(1);
  };

  return (
    <div
      className="absolute bg-white rounded-lg border-2 border-gray-300 shadow-lg p-4 min-w-[200px] select-none transition-shadow duration-200 cursor-grab hover:shadow-xl"
      style={{
        left: trigger.position.x,
        top: trigger.position.y,
        zIndex: 1
      }}
      onMouseDown={(e) => onMouseDown(e, trigger.id)}
    >
      {/* Settings Icon */}
      <button
        className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSettingsClick(trigger.id);
        }}
        title="Configure trigger"
      >
        <Settings className="h-3 w-3 text-gray-600" />
      </button>

      <div className="flex items-center space-x-3 pointer-events-none">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Zap className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{trigger.name}</h4>
          <p className="text-xs text-gray-500">{formatEventType(trigger.config?.eventType || 'created', trigger.type)}</p>
          {trigger.config?.uiLabel && (
            <p className="text-xs text-purple-600 font-medium mt-1 bg-purple-50 px-2 py-1 rounded-md">
              {trigger.config.uiLabel}
            </p>
          )}
        </div>
      </div>

      {/* Outflow Port */}
      <ConnectionPort
        nodeId={trigger.id}
        type="outflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
      />
    </div>
  );
};

// Action Node Component
interface ActionNodeComponentProps {
  action: ActionNodeType;
  onMouseDown: (e: React.MouseEvent, actionId: string) => void;
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onSettingsClick: (actionId: string) => void;
  onWireDropped: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
}

const ActionNode: React.FC<ActionNodeComponentProps> = ({ 
  action, 
  onMouseDown, 
  onStartWireDrag, 
  onSettingsClick,
  onWireDropped
}) => {
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'sms':
        return <MessageCircle className="h-5 w-5 text-green-600" />;
      case 'email':
        return <Mail className="h-5 w-5 text-blue-600" />;
      case 'delay':
        return <Clock className="h-5 w-5 text-orange-600" />;
      case 'create_crm_lead':
        return <Plus className="h-5 w-5 text-purple-600" />;
      case 'check':
        return <Eye className="h-5 w-5 text-yellow-600" />;
      case 'create_task':
        return <CheckSquare className="h-5 w-5 text-indigo-600" />;
      default:
        return '⚡';
    }
  };

  return (
    <div
      className="absolute bg-white rounded-lg border-2 border-gray-300 shadow-lg p-4 min-w-[200px] select-none transition-shadow duration-200 cursor-grab hover:shadow-xl"
      style={{
        left: action.position.x,
        top: action.position.y,
        zIndex: 1
      }}
      onMouseDown={(e) => onMouseDown(e, action.id)}
    >
      {/* Settings Icon */}
      <button
        className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSettingsClick(action.id);
        }}
        title="Configure action"
      >
        <Settings className="h-3 w-3 text-gray-600" />
      </button>

      {/* Inflow Port */}
      <ConnectionPort
        nodeId={action.id}
        type="inflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
      />

      <div className="flex items-center space-x-3 pointer-events-none">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          {getActionIcon(action.type)}
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{action.name}</h4>
          <p className="text-xs text-gray-500">Action</p>
          {action.config?.uiLabel && (
            <p className="text-xs text-green-600 font-medium mt-1 bg-green-50 px-2 py-1 rounded-md">
              {action.config.uiLabel}
            </p>
          )}
        </div>
      </div>

      {/* Outflow Port */}
      <ConnectionPort
        nodeId={action.id}
        type="outflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
      />
    </div>
  );
};

const AutomationsV2 = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTriggerLibraryOpen, setIsTriggerLibraryOpen] = useState(false);
  const [isActionLibraryOpen, setIsActionLibraryOpen] = useState(false);
  const [isTriggerSettingsOpen, setIsTriggerSettingsOpen] = useState(false);
  const [selectedTriggerId, setSelectedTriggerId] = useState<string | null>(null);
  const [automationTitle, setAutomationTitle] = useState('');
  const [automations, setAutomations] = useState<AutomationV2[]>([]);
  const [selectedAutomation, setSelectedAutomation] = useState<AutomationV2 | null>(null);
  const [triggers, setTriggers] = useState<TriggerNodeType[]>([]);
  const [actions, setActions] = useState<ActionNodeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [wireState, setWireState] = useState<WireState>({
    isDragging: false,
    startNodeId: null,
    startPosition: { x: 0, y: 0 },
    currentPosition: { x: 0, y: 0 }
  });
  
  // New state for execution history
  const [activeTab, setActiveTab] = useState<'workflow' | 'workflow-new' | 'history'>('workflow-new');
  
  // Linear view connection state
  const [selectedOutflowPort, setSelectedOutflowPort] = useState<string | null>(null);
  const [pendingActionParent, setPendingActionParent] = useState<string | null>(null);
  const [disconnectPopup, setDisconnectPopup] = useState<{
    show: boolean;
    nodeId: string;
    portType: 'inflow' | 'outflow';
    position: { x: number; y: number };
  } | null>(null);
  const [executions, setExecutions] = useState<AutomationExecutionV2[]>([]);
  const [executionSummary, setExecutionSummary] = useState<{
    running_executions: number;
    successful_executions: number;
    failed_executions: number;
    last_execution?: AutomationExecutionV2;
  } | null>(null);
  
  // Execution details modal state
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [executionSteps, setExecutionSteps] = useState<AutomationActionExecutionV2[]>([]);
  const [isExecutionDetailsOpen, setIsExecutionDetailsOpen] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState(false);

  // State for permanent wire connections
  const [wireConnections, setWireConnections] = useState<WireConnection[]>([]);
  
  // Flag to prevent debounced saves during immediate saves
  const [isSavingImmediately, setIsSavingImmediately] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const wireRerenderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { selectedBusiness } = useBusiness();

  // Available trigger types
  const availableTriggers = [
    {
      id: 'booking',
      name: 'Booking',
      description: 'Triggers when a booking is created, updated, or cancelled',
      icon: <Zap className="h-6 w-6 text-purple-600" />,
      color: 'bg-blue-100 border-blue-300'
    },
    {
      id: 'form_submission',
      name: 'Form Submission',
      description: 'Triggers when a form is submitted by a customer',
      icon: <Plus className="h-6 w-6 text-orange-600" />,
      color: 'bg-orange-100 border-orange-300'
    }
  ];

  // Available action types
  const availableActions = [
    {
      id: 'sms',
      name: 'SMS',
      description: 'Send an SMS message to customers or team members',
      icon: <MessageCircle className="h-6 w-6 text-green-600" />,
      color: 'bg-green-100 border-green-300'
    },
    {
      id: 'email',
      name: 'Email',
      description: 'Send an email to customers or team members via Gmail',
      icon: <Mail className="h-6 w-6 text-blue-600" />,
      color: 'bg-blue-100 border-blue-300'
    },
    {
      id: 'delay',
      name: 'Wait',
      description: 'Pause the automation for a specified amount of time',
      icon: <Clock className="h-6 w-6 text-orange-600" />,
      color: 'bg-orange-100 border-orange-300'
    },
    {
      id: 'create_crm_lead',
      name: 'Create CRM Lead',
      description: 'Create a new lead card in a CRM pipeline',
      icon: <Plus className="h-6 w-6 text-purple-600" />,
      color: 'bg-purple-100 border-purple-300'
    },
    {
      id: 'check',
      name: 'Check',
      description: 'Check the status of feedback or other conditions',
      icon: <Eye className="h-6 w-6 text-yellow-600" />,
      color: 'bg-yellow-100 border-yellow-300'
    },
    {
      id: 'create_task',
      name: 'Create Task',
      description: 'Create a new task for team members',
      icon: <CheckSquare className="h-6 w-6 text-indigo-600" />,
      color: 'bg-indigo-100 border-indigo-300'
    }
  ];

  // New state for trigger settings
  const [triggerSettingsModal, setTriggerSettingsModal] = useState<{
    isOpen: boolean;
    triggerId: string | null;
    triggerType: string;
    eventType: string;
  }>({
    isOpen: false,
    triggerId: null,
    triggerType: '',
    eventType: 'created'
  });

  // New state for SMS action settings
  const [smsActionSettingsModal, setSmsActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    message: string;
    recipient: string;
    customPhone: string;
  }>({
    isOpen: false,
    actionId: null,
    message: '',
    recipient: '',
    customPhone: ''
  });

  // New state for Email action settings
  const [emailActionSettingsModal, setEmailActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    subject: string;
    message: string;
    recipient: string;
    customEmail: string;
    useTemplate: boolean;
    selectedTemplateId: string | null;
  }>({
    isOpen: false,
    actionId: null,
    subject: '',
    message: '',
    recipient: '',
    customEmail: '',
    useTemplate: false,
    selectedTemplateId: null
  });

  // New state for Email Template Selector Modal
  const [emailTemplateSelector, setEmailTemplateSelector] = useState<{
    isOpen: boolean;
    templates: any[];
    loading: boolean;
  }>({
    isOpen: false,
    templates: [],
    loading: false
  });

  // New state for Delay action settings
  const [delayActionSettingsModal, setDelayActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    waitType: 'duration' | 'reminder';
    duration: number;
    unit: 'minutes' | 'hours' | 'days';
    reminderHoursBefore: number;
    reminderReference: 'drop_off_datetime' | 'start_datetime';
  }>({
    isOpen: false,
    actionId: null,
    waitType: 'duration',
    duration: 1,
    unit: 'hours',
    reminderHoursBefore: 24,
    reminderReference: 'drop_off_datetime'
  });

  // New state for Create CRM Lead action settings
  const [crmLeadActionSettingsModal, setCrmLeadActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
  }>({
    isOpen: false,
    actionId: null
  });

  // New state for Check action settings
  const [checkActionSettingsModal, setCheckActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    checkType: 'feedback';
    ifCompleted: 'stop' | 'continue';
    ifNotCompleted: 'stop' | 'continue';
  }>({
    isOpen: false,
    actionId: null,
    checkType: 'feedback',
    ifCompleted: 'stop',
    ifNotCompleted: 'continue'
  });

  // New state for Create Task action settings
  const [createTaskActionSettingsModal, setCreateTaskActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    selectedBoardId: string;
    selectedColumnId: string;
    taskTitle: string;
    taskDescription: string;
    taskUrgency: 'low' | 'medium' | 'high' | 'alert';
    taskStatus: 'pending' | 'scheduled';
    scheduleOffset: number;
    scheduleUnit: 'hours' | 'days' | 'weeks';
    boards: Array<{id: string, board_title: string}>;
    columns: Array<{id: string, title: string}>;
  }>({
    isOpen: false,
    actionId: null,
    selectedBoardId: '',
    selectedColumnId: '',
    taskTitle: '',
    taskDescription: '',
    taskUrgency: 'medium',
    taskStatus: 'pending',
    scheduleOffset: 1,
    scheduleUnit: 'days',
    boards: [],
    columns: []
  });

  // Task Components Modal State
  const [taskComponentsModal, setTaskComponentsModal] = useState<{
    isOpen: boolean;
    currentDescription: string;
  }>({
    isOpen: false,
    currentDescription: ''
  });

  // New state for Variables modal
  const [variablesModal, setVariablesModal] = useState<{
    isOpen: boolean;
    currentField: string | null;
    currentValue: string;
  }>({
    isOpen: false,
    currentField: null,
    currentValue: ''
  });

  // State for edit automation title modal
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  
  // State for delete automation modal
  const [isDeleteAutomationModalOpen, setIsDeleteAutomationModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Add testing state
  const [isTesting, setIsTesting] = useState(false);

  // State for lead forms
  const [leadForms, setLeadForms] = useState<any[]>([]);
  const [loadingLeadForms, setLoadingLeadForms] = useState(false);

  // State for CRM mapping  
  const [pipelines, setPipelines] = useState<Array<{id: number, pipeline_name: string, stages: Array<{id: number, name: string}>}>>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [selectedFormFields, setSelectedFormFields] = useState<Array<{id: string, type: string, label: string}>>([]);
  const [fieldMappings, setFieldMappings] = useState<{
    selectedPipeline: number | null;
    selectedPipelineName: string;
    selectedStage: number | null;
    selectedStageName: string;
    title: string;
    value: string;
    business_name: string;
    source: string;
    tags: string;
    custom_fields: { [key: string]: string };
  }>({
    selectedPipeline: null,
    selectedPipelineName: '',
    selectedStage: null,
    selectedStageName: '',
    title: '',
    value: '',
    business_name: '',
    source: '',
    tags: '',
    custom_fields: {}
  });

  // Custom fields state for CRM Lead creation
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<Array<{id: string, name: string, type: string}>>([]);

  // References for message textareas to insert variables
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const emailMessageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const emailSubjectInputRef = useRef<HTMLInputElement>(null);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load automations when component mounts or business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadAutomations();
    }
  }, [selectedBusiness?.id]);

  // Load triggers and actions when automation changes
  useEffect(() => {
    if (selectedAutomation?.id) {
      loadTriggers();
      loadActions();
      loadWireConnections();
    } else {
      setTriggers([]);
      setActions([]);
      setWireConnections([]);
    }
  }, [selectedAutomation?.id]);

  // Load execution history when tab changes to history
  useEffect(() => {
    if (selectedAutomation?.id && activeTab === 'history') {
      loadExecutionHistory();
    }
  }, [selectedAutomation?.id, activeTab]);



  // ResizeObserver to detect when nodes finish layout changes
  useEffect(() => {
    if (!canvasRef.current || triggers.length === 0 && actions.length === 0) return;

    const observer = new ResizeObserver((entries) => {
      // Debounce the wire re-render to avoid excessive updates
      if (wireRerenderTimeoutRef.current) {
        clearTimeout(wireRerenderTimeoutRef.current);
      }
      wireRerenderTimeoutRef.current = setTimeout(() => {
        console.log('📐 ResizeObserver detected layout changes - updating wires');
        setWireConnections(prev => [...prev]);
      }, 100);
    });

    // Observe all node elements for layout changes
    const nodeElements = canvasRef.current.querySelectorAll('[data-node-id]');
    nodeElements.forEach(element => observer.observe(element));

    return () => {
      observer.disconnect();
      if (wireRerenderTimeoutRef.current) {
        clearTimeout(wireRerenderTimeoutRef.current);
      }
    };
  }, [triggers.length, actions.length]);

  // Multiple trigger approach for wire positioning
  useEffect(() => {
    if (triggers.length > 0 || actions.length > 0) {
      // Immediate attempt (for cases where DOM is ready)
      setTimeout(() => {
        console.log('🔗 Immediate wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 50);

      // Delayed attempt (for slower rendering)
      setTimeout(() => {
        console.log('🔗 Delayed wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 300);

      // Final attempt (fallback)
      setTimeout(() => {
        console.log('🔗 Final wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 800);
    }
  }, [triggers.length, actions.length]);

  // Also re-render when wire connections are first loaded
  useEffect(() => {
    if (wireConnections.length > 0) {
      setTimeout(() => {
        console.log('🔗 Wire connections loaded - positioning update');
        setWireConnections(prev => [...prev]);
      }, 100);
    }
  }, [wireConnections.length]);

  // Save triggers to database when triggers change (with debounce)
  useEffect(() => {
    if (selectedAutomation?.id && triggers.length >= 0) {
      const timeoutId = setTimeout(() => {
        saveTriggers();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [triggers, selectedAutomation?.id]);

  // Save actions to database when actions change (with debounce)
  useEffect(() => {
    if (selectedAutomation?.id && actions.length >= 0) {
      const timeoutId = setTimeout(() => {
        saveActions();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [actions, selectedAutomation?.id]);

  // Save wire connections to database when connections change (with debounce)
  useEffect(() => {
    if (selectedAutomation?.id && wireConnections.length >= 0 && !isSavingImmediately) {
      const timeoutId = setTimeout(() => {
        saveConnections();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [wireConnections, selectedAutomation?.id, isSavingImmediately]);

  // Handle mouse events for node dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !draggedId || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const newX = Math.max(0, e.clientX - rect.left - dragOffset.x);
      const newY = Math.max(0, e.clientY - rect.top - dragOffset.y);

      // Update trigger positions
      setTriggers(prev => prev.map(trigger => 
        trigger.id === draggedId 
          ? { ...trigger, position: { x: newX, y: newY } }
          : trigger
      ));

      // Update action positions
      setActions(prev => prev.map(action => 
        action.id === draggedId 
          ? { ...action, position: { x: newX, y: newY } }
          : action
      ));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDraggedId(null);
      setDragOffset({ x: 0, y: 0 });
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, draggedId, dragOffset]);

  // Handle mouse events for wire dragging
  useEffect(() => {
    const handleWireMouseMove = (e: MouseEvent) => {
      if (!wireState.isDragging || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      setWireState(prev => ({
        ...prev,
        currentPosition: { x: currentX, y: currentY }
      }));
    };

    const handleWireMouseUp = () => {
      if (wireState.isDragging) {
        console.log('🔗 Wire drag ended - from:', wireState.startPosition, 'to:', wireState.currentPosition);
        setWireState({
          isDragging: false,
          startNodeId: null,
          startPosition: { x: 0, y: 0 },
          currentPosition: { x: 0, y: 0 }
        });
      }
    };

    if (wireState.isDragging) {
      document.addEventListener('mousemove', handleWireMouseMove);
      document.addEventListener('mouseup', handleWireMouseUp);
      document.body.style.cursor = 'crosshair';
      console.log('🔗 Wire dragging active - from node:', wireState.startNodeId);
    }

    return () => {
      document.removeEventListener('mousemove', handleWireMouseMove);
      document.removeEventListener('mouseup', handleWireMouseUp);
      if (wireState.isDragging) {
        document.body.style.cursor = '';
      }
    };
  }, [wireState.isDragging]);

  // Debug wire state changes
  useEffect(() => {
    console.log('🔗 WireState changed:', wireState);
    if (wireState.isDragging) {
      console.log('🔗 Wire should be visible now! Position from', wireState.startPosition, 'to', wireState.currentPosition);
    }
  }, [wireState]);

  const loadAutomations = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getAutomations(selectedBusiness.id);
      setAutomations(data);
    } catch (err) {
      console.error('Error loading automations:', err);
      setError('Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  const loadTriggers = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const data = await getAutomationTriggers(selectedAutomation.id);
      setTriggers(data);
    } catch (err) {
      console.error('Error loading triggers:', err);
      setError('Failed to load triggers');
    }
  };

  const loadActions = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const data = await getAutomationActions(selectedAutomation.id);
      setActions(data);
    } catch (err) {
      console.error('Error loading actions:', err);
      setError('Failed to load actions');
    }
  };

  const loadExecutionHistory = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const executionsData = await getAutomationExecutions(selectedAutomation.id);
      // Cast to the correct type since the data structure matches
      const typedExecutions = executionsData as unknown as AutomationExecutionV2[];
      setExecutions(typedExecutions);
      
      // Calculate summary using smart status logic
      const runningCount = typedExecutions.filter(exec => getActualExecutionStatus(exec) === 'running').length;
      const successfulCount = typedExecutions.filter(exec => getActualExecutionStatus(exec) === 'completed').length;
      const failedCount = typedExecutions.filter(exec => getActualExecutionStatus(exec) === 'failed').length;
      
      setExecutionSummary({
        running_executions: runningCount,
        successful_executions: successfulCount,
        failed_executions: failedCount,
        last_execution: typedExecutions[0] || undefined
      });
    } catch (err) {
      console.error('Error loading execution history:', err);
      setError('Failed to load execution history');
    }
  };

  const saveTriggers = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      await saveAutomationTriggers(selectedAutomation.id, triggers);
    } catch (err) {
      console.error('Error saving triggers:', err);
      setError('Failed to save triggers');
    }
  };

  const saveActions = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      await saveAutomationActions(selectedAutomation.id, actions);
    } catch (err) {
      console.error('Error saving actions:', err);
      setError('Failed to save actions');
    }
  };

  const saveConnections = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      await saveWireConnections(selectedAutomation.id, wireConnections);
      console.log('💾 Saved wire connections to database:', wireConnections.length);
    } catch (err) {
      console.error('Error saving wire connections:', err);
      setError('Failed to save wire connections');
    }
  };

  const loadExecutionSteps = async (executionId: string) => {
    try {
      setLoadingSteps(true);
      const steps = await getExecutionSteps(executionId);
      // Cast to the correct type since the data structure matches
      const typedSteps = steps as unknown as AutomationActionExecutionV2[];
      setExecutionSteps(typedSteps);
    } catch (err) {
      console.error('Error loading execution steps:', err);
      setError('Failed to load execution details');
    } finally {
      setLoadingSteps(false);
    }
  };

  const handleViewExecution = async (executionId: number) => {
    setSelectedExecutionId(executionId.toString());
    setIsExecutionDetailsOpen(true);
    await loadExecutionSteps(executionId.toString());
  };

  const handleCloseExecutionDetails = () => {
    setIsExecutionDetailsOpen(false);
    setSelectedExecutionId(null);
    setExecutionSteps([]);
  };

  // Helper function to format duration
  const formatDuration = (durationMs: number): string => {
    if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString: string): string => {
    const now = new Date().getTime();
    const date = new Date(dateString).getTime();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  // Helper function to calculate actual execution status based on action progress
  const getActualExecutionStatus = (execution: any): string => {
    // If there's an explicit error, it's failed
    if (execution.error_message || execution.execution_status === 'failed') {
      return 'failed';
    }
    
    // If no actions to execute, consider it completed
    if (execution.total_actions === 0) {
      return 'completed';
    }
    
    // If all actions are executed, it's completed
    if (execution.executed_actions >= execution.total_actions) {
      return 'completed';
    }
    
    // If some actions are executed but not all, it's running
    if (execution.executed_actions > 0) {
      return 'running';
    }
    
    // If no actions executed yet, check the database status
    if (execution.execution_status === 'pending') {
      return 'pending';
    }
    
    // Default to running if execution has started
    return 'running';
  };

  // Helper function to get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'pending':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const handleCreateClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setAutomationTitle('');
    setError(null);
  };

  const handleSaveAutomation = async () => {
    if (!automationTitle.trim() || !selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const newAutomation = await createAutomation({
        name: automationTitle.trim(),
        business_id: selectedBusiness.id
      });
      
      setAutomations(prev => [newAutomation, ...prev]);
      console.log('Created automation:', newAutomation);
      handleCloseModal();
    } catch (err) {
      console.error('Error creating automation:', err);
      setError('Failed to create automation');
    } finally {
      setLoading(false);
    }
  };

  const handleAutomationClick = (automation: AutomationV2) => {
    setSelectedAutomation(automation);
    setActiveTab('workflow-new'); // Reset to new workflow tab when changing automation
  };

  const handleToggleLive = async () => {
    if (!selectedAutomation) return;

    try {
      setError(null);
      const updatedAutomation = await toggleAutomationLive(
        selectedAutomation.id, 
        !selectedAutomation.is_live
      );

      setAutomations(prev => 
        prev.map(automation => 
          automation.id === selectedAutomation.id ? updatedAutomation : automation
        )
      );
      setSelectedAutomation(updatedAutomation);
    } catch (err) {
      console.error('Error toggling automation live status:', err);
      setError('Failed to update automation status');
    }
  };

  const handleAddTrigger = () => {
    setIsTriggerLibraryOpen(true);
  };

  const handleSelectTrigger = (triggerType: any) => {
    const newTrigger: TriggerNodeType = {
      id: `temp-${Date.now()}`,
      type: triggerType.id,
      name: triggerType.name,
      position: { x: 100, y: 100 },
      config: { eventType: 'created' }
    };

    setTriggers(prev => [...prev, newTrigger]);
    setIsTriggerLibraryOpen(false);
    console.log('Added trigger:', newTrigger);
  };


  const handleMouseDown = (e: React.MouseEvent, triggerId: string) => {
    e.preventDefault();
    
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const trigger = triggers.find(t => t.id === triggerId);
    
    if (!trigger) return;

    const offsetX = e.clientX - rect.left - trigger.position.x;
    const offsetY = e.clientY - rect.top - trigger.position.y;

    setIsDragging(true);
    setDraggedId(triggerId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleStartWireDrag = (nodeId: string, position: { x: number; y: number }) => {
    console.log(`🔗 Starting wire drag from node: ${nodeId} at position:`, position);
    setWireState({
      isDragging: true,
      startNodeId: nodeId,
      startPosition: position,
      currentPosition: position
    });
  };

  // Handle successful wire connection
  const handleWireDropped = async (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => {
    if (!wireState.isDragging || !wireState.startNodeId) return;
    
    // Prevent connecting to the same node
    if (wireState.startNodeId === targetNodeId) {
      console.log('🔗 Cannot connect node to itself');
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      return;
    }
    
    // Check if connection already exists
    const existingConnection = wireConnections.find(
      conn => conn.fromNodeId === wireState.startNodeId && conn.toNodeId === targetNodeId
    );
    
    if (existingConnection) {
      console.log('🔗 Connection already exists');
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      return;
    }
    
    try {
      // Set flag to prevent debounced saves
      setIsSavingImmediately(true);
      
      // Ensure all nodes are saved to database before creating connection
      console.log('🔗 Ensuring nodes are saved before creating connection...');
      if (selectedAutomation?.id) {
        await Promise.all([
          saveAutomationTriggers(selectedAutomation.id, triggers),
          saveAutomationActions(selectedAutomation.id, actions)
        ]);
        
        // Reload nodes to get database UUIDs (in case new nodes were created)
        const [updatedTriggers, updatedActions] = await Promise.all([
          getAutomationTriggers(selectedAutomation.id),
          getAutomationActions(selectedAutomation.id)
        ]);
        
        // Update local state with database UUIDs
        setTriggers(updatedTriggers);
        setActions(updatedActions);
        
        console.log('🔗 Nodes saved and reloaded with database UUIDs');
        
        // Map temporary IDs to database UUIDs
        const findActualNodeId = (tempId: string): string => {
          // Check if it's already a UUID
          if (tempId.length === 36 && tempId.includes('-')) {
            return tempId;
          }
          
          // Find in updated triggers
          const trigger = updatedTriggers.find(t => t.name === triggers.find(orig => orig.id === tempId)?.name);
          if (trigger) return trigger.id;
          
          // Find in updated actions  
          const action = updatedActions.find(a => a.name === actions.find(orig => orig.id === tempId)?.name);
          if (action) return action.id;
          
          return tempId; // Fallback to original ID
        };
        
        // Update the connection IDs to use database UUIDs
        const actualFromNodeId = findActualNodeId(wireState.startNodeId);
        const actualToNodeId = findActualNodeId(targetNodeId);
        
        console.log('🔗 Mapped IDs - From:', wireState.startNodeId, '→', actualFromNodeId, 'To:', targetNodeId, '→', actualToNodeId);
        
        // Update connection with actual database IDs
        const newConnection: WireConnection = {
          id: `wire-${Date.now()}`,
          fromNodeId: actualFromNodeId,
          toNodeId: actualToNodeId,
          fromPort: 'outflow',
          toPort: 'inflow'
        };
        
        setWireConnections(prev => [...prev, newConnection]);
        console.log('🔗 Created wire connection with database UUIDs:', newConnection);
        
        // Save connection to database with actual UUIDs
        await createAutomationConnection({
          automation_id: selectedAutomation.id,
          source_node_id: actualFromNodeId,
          target_node_id: actualToNodeId
        });
        console.log('🔗 Connection saved to database successfully');
      }
      
      // Clear the flag after a short delay to allow debounced saves to resume
      setTimeout(() => {
        setIsSavingImmediately(false);
      }, 1000);
      
      

    } catch (err) {
      console.error('Error creating connection:', err);
      setError('Failed to save wire connection');
      // Clear flag even on error
      setIsSavingImmediately(false);
    }
    
    // Clear wire drag state
    setWireState({
      isDragging: false,
      startNodeId: null,
      startPosition: { x: 0, y: 0 },
      currentPosition: { x: 0, y: 0 }
    });
  };

  const handleTriggerSettings = (triggerId: string) => {
    setSelectedTriggerId(triggerId);
    setIsTriggerSettingsOpen(true);
  };

  const handleCloseTriggerSettings = () => {
    setIsTriggerSettingsOpen(false);
    setSelectedTriggerId(null);
  };

  const handleDeleteTrigger = (triggerId: string) => {
    // Remove the trigger from the triggers array
    setTriggers(prev => prev.filter(trigger => trigger.id !== triggerId));
    
    // Remove any wire connections involving this trigger
    setWireConnections(prev => prev.filter(connection => 
      connection.fromNodeId !== triggerId && connection.toNodeId !== triggerId
    ));
    
    // Close the settings modal
    handleCloseTriggerSettings();
    
    console.log('Trigger and related connections deleted:', triggerId);
  };

  // Action handlers
  const handleAddAction = () => {
    setIsActionLibraryOpen(true);
  };

  // Helper function to find the last node in the workflow chain
  const findLastNodeInWorkflow = (): string | null => {
    // First, try to find a node that has no outgoing connections (end of chain)
    const allNodes = [...triggers.map(t => t.id), ...actions.map(a => a.id)];
    const nodesWithOutgoingConnections = new Set(wireConnections.map(conn => conn.fromNodeId));
    
    // Find nodes that don't have outgoing connections
    const endNodes = allNodes.filter(nodeId => !nodesWithOutgoingConnections.has(nodeId));
    
    if (endNodes.length > 0) {
      // If there are multiple end nodes, prefer actions over triggers
      const endActions = endNodes.filter(nodeId => actions.some(a => a.id === nodeId));
      if (endActions.length > 0) {
        return endActions[0]; // Return the first end action
      }
      return endNodes[0]; // Return the first end node (could be a trigger)
    }
    
    // If no end nodes found (circular connections), try to find the most recently added node
    if (actions.length > 0) {
      return actions[actions.length - 1].id;
    }
    
    if (triggers.length > 0) {
      return triggers[triggers.length - 1].id;
    }
    
    return null;
  };

  const handleSelectAction = async (actionType: any) => {
    // Create a unique temporary ID that includes timestamp and random component
    const uniqueId = `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newAction: ActionNodeType = {
      id: uniqueId,
      type: actionType.id,
      name: actionType.name,
      position: { x: 400, y: 100 }, // Position to the right of triggers
      config: {}
    };

    setActions(prev => [...prev, newAction]);
    setIsActionLibraryOpen(false);
    
    // Save the new action to database without auto-connecting
    if (selectedAutomation?.id) {
      try {
        console.log('💾 Saving new action to database:', uniqueId);
        
        // Save all nodes to database to get real UUIDs
        const currentActions = [...actions, newAction];
        await Promise.all([
          saveAutomationTriggers(selectedAutomation.id, triggers),
          saveAutomationActions(selectedAutomation.id, currentActions)
        ]);
        
        // Reload nodes to get database UUIDs and update local state
        const [updatedTriggers, updatedActions] = await Promise.all([
          getAutomationTriggers(selectedAutomation.id),
          getAutomationActions(selectedAutomation.id)
        ]);
        
        setTriggers(updatedTriggers);
        setActions(updatedActions);
        
        console.log('✅ Action saved successfully - ready for manual connection');
      } catch (err) {
        console.error('❌ Error saving action:', err);
        setError('Failed to save action');
      }
    }
    
    // Clear pending parent state
    setPendingActionParent(null);
    
    console.log('✅ Added action (manual connection required):', newAction);
  };

  const handleActionMouseDown = (e: React.MouseEvent, actionId: string) => {
    e.preventDefault();
    
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const action = actions.find(a => a.id === actionId);
    
    if (!action) return;

    const offsetX = e.clientX - rect.left - action.position.x;
    const offsetY = e.clientY - rect.top - action.position.y;

    setIsDragging(true);
    setDraggedId(actionId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  // Handle opening action settings
  const handleActionSettingsClick = (actionId: string) => {
    const currentAction = actions.find(action => action.id === actionId);
    
    if (currentAction?.type === 'sms') {
      // SMS Action Settings
      const existingMessage = currentAction?.config?.message || '';
      const existingRecipient = currentAction?.config?.recipient || '';
      const existingCustomPhone = currentAction?.config?.customPhone || '';
      
      setSmsActionSettingsModal({
        isOpen: true,
        actionId,
        message: existingMessage,
        recipient: existingRecipient,
        customPhone: existingCustomPhone
      });
    } else if (currentAction?.type === 'email') {
      // Email Action Settings
      const existingSubject = currentAction?.config?.subject || '';
      const existingMessage = currentAction?.config?.message || '';
      const existingRecipient = currentAction?.config?.recipient || '';
      const existingCustomEmail = currentAction?.config?.customEmail || '';
      const existingUseTemplate = currentAction?.config?.useTemplate || false;
      const existingSelectedTemplateId = currentAction?.config?.selectedTemplateId || null;
      
      setEmailActionSettingsModal({
        isOpen: true,
        actionId,
        subject: existingSubject,
        message: existingMessage,
        recipient: existingRecipient,
        customEmail: existingCustomEmail,
        useTemplate: existingUseTemplate,
        selectedTemplateId: existingSelectedTemplateId
      });
    } else if (currentAction?.type === 'delay') {
      // Delay Action Settings
      const existingDuration = currentAction?.config?.displayDuration || currentAction?.config?.duration || 1;
      const existingUnit = currentAction?.config?.unit || 'hours';
      const existingWaitType = currentAction?.config?.wait_type || 'duration';
      const existingReminderHours = currentAction?.config?.reminder_hours_before || 24;
      const existingReminderReference = currentAction?.config?.reminder_reference || 'drop_off_datetime';
      
      setDelayActionSettingsModal({
        isOpen: true,
        actionId,
        waitType: existingWaitType,
        duration: existingDuration,
        unit: existingUnit,
        reminderHoursBefore: existingReminderHours,
        reminderReference: existingReminderReference
      });
    } else if (currentAction?.type === 'create_crm_lead') {
      // Create CRM Lead Action Settings
      setCrmLeadActionSettingsModal({
        isOpen: true,
        actionId
      });
      
      // Load necessary data
      fetchPipelines();
      fetchLeadForms(); // Also fetch lead forms for variables
      loadCustomFieldDefinitions(); // Load custom field definitions
      
      // Set form fields after a brief delay to allow forms to load
      setTimeout(() => {
        const formFields = getSelectedFormFields();
        setSelectedFormFields(formFields);
      }, 100);
      
      // Always reset field mappings, then load existing configuration if any
      const config = currentAction?.config || {};
      
      // Convert values to strings properly
      const titleValue = typeof config?.title === 'object' ? (config.title?.value || '') : (config?.title || '');
      const valueValue = typeof config?.value === 'object' ? (config.value?.value || '') : (config?.value || '');
      const businessNameValue = typeof config?.business_name === 'object' ? (config.business_name?.value || '') : (config?.business_name || '');
      const sourceValue = typeof config?.source === 'object' ? (config.source?.value || '') : (config?.source || '');
      const tagsValue = typeof config?.tags === 'object' ? (config.tags?.value || '') : (config?.tags || '');
      const statusValue = typeof config?.status === 'object' ? (config.status?.value || 'Open') : (config?.status || 'Open');
      
      // Handle custom fields - convert from old object format or use existing object format
      let customFieldsValue = {};
      if (config?.custom_fields) {
        if (typeof config.custom_fields === 'string') {
          try {
            customFieldsValue = JSON.parse(config.custom_fields);
          } catch (e) {
            console.warn('Failed to parse custom_fields JSON:', e);
          }
        } else if (typeof config.custom_fields === 'object') {
          customFieldsValue = config.custom_fields;
        }
      }
      
      setFieldMappings(prev => ({
        ...prev,
        selectedPipeline: config?.selectedPipeline || null,
        selectedPipelineName: config?.selectedPipelineName || '',
        selectedStage: config?.selectedStage || null,
        selectedStageName: config?.selectedStageName || '',
        title: titleValue,
        value: valueValue,
        business_name: businessNameValue,
        source: sourceValue,
        tags: tagsValue,
        status: statusValue,
        custom_fields: customFieldsValue
      }));
    } else if (currentAction?.type === 'check') {
      // Check Action Settings
      const existingCheckType = currentAction?.config?.checkType || 'feedback';
      
      setCheckActionSettingsModal({
        isOpen: true,
        actionId,
        checkType: existingCheckType,
        ifCompleted: currentAction?.config?.ifCompleted || 'stop',
        ifNotCompleted: currentAction?.config?.ifNotCompleted || 'continue'
      });
    } else if (currentAction?.type === 'create_task') {
      // Create Task Action Settings
      const existingBoardId = currentAction?.config?.selectedBoardId || '';
      const existingColumnId = currentAction?.config?.selectedColumnId || '';
      const existingTaskTitle = currentAction?.config?.taskTitle || '';
      const existingTaskDescription = currentAction?.config?.taskDescription || '';
      const existingTaskUrgency = currentAction?.config?.taskUrgency || 'medium';
      
      setCreateTaskActionSettingsModal({
        isOpen: true,
        actionId,
        selectedBoardId: existingBoardId,
        selectedColumnId: existingColumnId,
        taskTitle: existingTaskTitle,
        taskDescription: existingTaskDescription,
        taskUrgency: existingTaskUrgency,
        taskStatus: currentAction?.config?.taskStatus || 'pending',
        scheduleOffset: currentAction?.config?.scheduleOffset || 1,
        scheduleUnit: currentAction?.config?.scheduleUnit || 'days',
        boards: [],
        columns: []
      });
      
      // Load boards and columns with a delay to ensure state is updated
      setTimeout(async () => {
        await loadBoardsAndColumns();
        
        // If there's a saved board ID, load its columns
        if (existingBoardId) {
          try {
            const boardWithColumns = await getBoardWithColumns(existingBoardId);
            setCreateTaskActionSettingsModal(prev => ({
              ...prev,
              columns: boardWithColumns.columns.map(column => ({
                id: column.id,
                title: column.title
              }))
            }));
          } catch (error) {
            console.error('Error loading columns for existing board:', error);
          }
        }
      }, 100);
    }
  };

  // Helper function to check if wait action is connected to a "created" trigger
  const isConnectedToCreatedTrigger = (actionId: string): boolean => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    
    // Check if any connected trigger is a booking trigger with any booking event type
    return connectedTriggers.some(trigger => 
      trigger?.type === 'booking'
    );
  };

  // Handle deleting action from settings modal
  const handleDeleteActionFromSettings = async () => {
    if (!smsActionSettingsModal.actionId) return;

    try {
      const actionId = smsActionSettingsModal.actionId;
      
      // Remove action from local state
      setActions(prev => prev.filter(action => action.id !== actionId));
      
      // Remove any wire connections involving this action
      setWireConnections(prev => prev.filter(connection => 
        connection.fromNodeId !== actionId && connection.toNodeId !== actionId
      ));
      
      // Close modal
      setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
      
      console.log('Action and related connections deleted:', actionId);
    } catch (error) {
      console.error('Error deleting action:', error);
    }
  };

  // Get available notification recipients based on connected triggers/actions (with chain traversal)
  const getAvailableRecipients = (actionId: string): Array<{value: string, label: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const recipients: Array<{value: string, label: string}> = [];
    
    // Add recipients based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking') {
        if (!recipients.some(r => r.value === 'customer_phone')) {
          recipients.push({ value: 'customer_phone', label: 'Customer Phone' });
        }
        if (!recipients.some(r => r.value === 'resource_phone')) {
          recipients.push({ value: 'resource_phone', label: 'Resource Phone' });
        }
        // Note: Business Phone removed for booking triggers - not typically needed for booking notifications
      } else if (trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form') {
        // For form submission triggers, customer phone is the primary recipient
        const selectedFormId = trigger.config?.selectedForm || trigger.config?.selectedFormId || trigger.config?.formId;
        
        if (selectedFormId) {
          if (!recipients.some(r => r.value === 'customer_phone')) {
            recipients.push({ value: 'customer_phone', label: 'Customer Phone (from form)' });
          }
        }
      }
    });
    
    // Always allow custom phone number option
    recipients.push({ value: 'custom', label: 'Custom Phone Number' });
    
    return recipients;
  };

  // Get available email recipients based on connected triggers/actions (similar to SMS but for emails)
  const getAvailableEmailRecipients = (actionId: string): Array<{value: string, label: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const recipients: Array<{value: string, label: string}> = [];
    
    // Add recipients based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking') {
        if (!recipients.some(r => r.value === 'customer_email')) {
          recipients.push({ value: 'customer_email', label: 'Customer Email' });
        }
        if (!recipients.some(r => r.value === 'business_email')) {
          recipients.push({ value: 'business_email', label: 'Business Email' });
        }
      } else if (trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form') {
        // For form submission triggers, customer email is the primary recipient
        const selectedFormId = trigger.config?.selectedForm || trigger.config?.selectedFormId || trigger.config?.formId;
        
        if (selectedFormId) {
          if (!recipients.some(r => r.value === 'customer_email')) {
            recipients.push({ value: 'customer_email', label: 'Customer Email (from form)' });
          }
        }
      }
    });
    
    // Always allow custom email address option
    recipients.push({ value: 'custom', label: 'Custom Email Address' });
    
    return recipients;
  };

  // Get available variables based on connected triggers/actions (with chain traversal)
  const getAvailableVariables = (actionId: string): Array<{value: string, label: string, description: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };

    // Traverse the connection chain backwards to find Create CRM Lead actions
    const findCrmLeadActionsInChain = (nodeId: string, visited = new Set<string>()): ActionNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a Create CRM Lead action
      const directAction = actions.find(action => action.id === nodeId && action.type === 'create_crm_lead');
      const foundActions: ActionNodeType[] = directAction ? [directAction] : [];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const actionsFromUpstream = findCrmLeadActionsInChain(connection.fromNodeId, visited);
        foundActions.push(...actionsFromUpstream);
      }
      
      return foundActions;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const connectedCrmLeadActions = findCrmLeadActionsInChain(actionId);
    const variables: Array<{value: string, label: string, description: string}> = [];
    
    // Add variables based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking') {
        // Customer variables
        variables.push(
          { value: '{customer_name}', label: 'Customer Name', description: 'Full name of the customer' },
          { value: '{first_name}', label: 'First Name', description: 'Customer\'s first name' },
          { value: '{last_name}', label: 'Last Name', description: 'Customer\'s last name' },
          { value: '{customer_phone}', label: 'Customer Phone', description: 'Customer\'s phone number' },
          { value: '{customer_email}', label: 'Customer Email', description: 'Customer\'s email address' }
        );
        
        // Booking variables
        variables.push(
          { value: '{booking_number}', label: 'Booking Number', description: 'Unique booking number' },
          { value: '{booking_title}', label: 'Booking Title', description: 'Title/name of the booking' },
          { value: '{grand_total}', label: 'Grand Total', description: 'Total cost of the booking' }
        );
        
        // Schedule variables
        variables.push(
          { value: '{start_date}', label: 'Start Date', description: 'Booking start date' },
          { value: '{start_time}', label: 'Start Time', description: 'Booking start time' },
          { value: '{drop_off_date}', label: 'Drop Off Date', description: 'Drop off date' },
          { value: '{drop_off_time}', label: 'Drop Off Time', description: 'Drop off time' }
        );
        
        // Vehicle variables
        variables.push(
          { value: '{vehicle_make}', label: 'Vehicle Make', description: 'Make of the customer\'s vehicle (e.g., Toyota)' },
          { value: '{vehicle_model}', label: 'Vehicle Model', description: 'Model of the customer\'s vehicle (e.g., Camry)' },
          { value: '{vehicle_year}', label: 'Vehicle Year', description: 'Year of the customer\'s vehicle' },
          { value: '{vehicle_color}', label: 'Vehicle Color', description: 'Color of the customer\'s vehicle' },
          { value: '{vehicle_info}', label: 'Vehicle Info', description: 'Full vehicle information (Year Make Model)' }
        );
        
        // Payment variables - only for payment-related triggers
        if (trigger.config?.eventType === 'partially_paid' || trigger.config?.eventType === 'paid') {
          variables.push(
            { value: '{invoice_link}', label: 'Invoice Link', description: 'Link to view the most recent invoice/receipt (only available for payment triggers)' }
          );
        }
        
        // Feedback link variable - only for completed booking triggers
        if (trigger.config?.eventType === 'completed') {
          variables.push(
            { value: '{feedback_link}', label: 'Feedback Link', description: 'Link for customer to leave feedback (only available for completed bookings)' }
          );
        }
      } else if (trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form') {
        // Handle form submission triggers
        const selectedFormId = trigger.config?.selectedForm || trigger.config?.selectedFormId || trigger.config?.formId;
        
        if (!selectedFormId) {
          // No form selected - no variables available
          console.log('⚠️ Form submission trigger found but no form selected');
          return;
        }
        
        // Find the selected lead form
        const selectedForm = leadForms.find(form => form.id === selectedFormId);
        
        if (!selectedForm?.form_config?.fields) {
          console.log('⚠️ Selected form not found or has no fields');
          return;
        }
        
        console.log('✅ Adding variables for form submission trigger with form:', selectedForm.name);
        
        // Process each field in the form to create variables
        selectedForm.form_config.fields.forEach((field: any) => {
          if (field.type === 'customer_info') {
            // Special handling for Customer Info component - add standardized customer fields
            variables.push(
              { value: '{first_name}', label: 'First Name', description: 'Customer\'s first name from the form' },
              { value: '{last_name}', label: 'Last Name', description: 'Customer\'s last name from the form' },
              { value: '{email}', label: 'Email Address', description: 'Customer\'s email address from the form' },
              { value: '{phone}', label: 'Phone Number', description: 'Customer\'s phone number from the form' },
              { value: '{customer_name}', label: 'Full Name', description: 'Customer\'s full name (first + last)' }
            );
          } else if (field.label) {
            // Regular form field - create variable from field label
            const variableName = field.label.toLowerCase()
              .replace(/[^a-z0-9\s]/g, '') // Remove special characters
              .replace(/\s+/g, '_') // Replace spaces with underscores
              .replace(/_+/g, '_') // Remove duplicate underscores
              .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
            
            variables.push({
              value: `{${variableName}}`,
              label: field.label,
              description: `Value from the "${field.label}" field in the form`
            });
          }
        });
      }
    });

    // Add variables from Create CRM Lead actions
    if (connectedCrmLeadActions.length > 0) {
      console.log('✅ Found Create CRM Lead action(s) in chain, adding CRM variables');
      variables.push(
        { value: '{pipeline_name}', label: 'Pipeline Name', description: 'Name of the CRM pipeline where the lead was created' },
        { value: '{stage_name}', label: 'Stage Name', description: 'Name of the CRM stage where the lead was placed' },
        { value: '{status}', label: 'Lead Status', description: 'Status of the created CRM lead (e.g., Open, In Progress)' }
      );
    }
    
    return variables;
  };

  // Insert variable into SMS message at cursor position
  const insertVariable = (variable: string) => {
    if (!messageTextareaRef.current) return;
    
    const textarea = messageTextareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentMessage = smsActionSettingsModal.message;
    
    // Insert the variable at cursor position
    const newMessage = currentMessage.substring(0, start) + variable + currentMessage.substring(end);
    
    // Update the message
    setSmsActionSettingsModal(prev => ({ ...prev, message: newMessage }));
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Insert variable into Email subject at cursor position
  const insertEmailSubjectVariable = (variable: string) => {
    if (!emailSubjectInputRef.current) return;
    
    const input = emailSubjectInputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentSubject = emailActionSettingsModal.subject;
    
    // Insert the variable at cursor position
    const newSubject = currentSubject.substring(0, start) + variable + currentSubject.substring(end);
    
    // Update the subject
    setEmailActionSettingsModal(prev => ({ ...prev, subject: newSubject }));
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Insert variable into Email message at cursor position
  const insertEmailMessageVariable = (variable: string) => {
    if (!emailMessageTextareaRef.current) return;
    
    const textarea = emailMessageTextareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentMessage = emailActionSettingsModal.message;
    
    // Insert the variable at cursor position
    const newMessage = currentMessage.substring(0, start) + variable + currentMessage.substring(end);
    
    // Update the message
    setEmailActionSettingsModal(prev => ({ ...prev, message: newMessage }));
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Handle saving SMS message
  const handleSaveSmsMessage = () => {
    if (!smsActionSettingsModal.actionId) return;

    // Update the action with the new message and recipient
    setActions(prev => prev.map(action => 
      action.id === smsActionSettingsModal.actionId
        ? { 
            ...action, 
            config: { 
              ...action.config, 
              message: smsActionSettingsModal.message,
              recipient: smsActionSettingsModal.recipient,
              customPhone: smsActionSettingsModal.customPhone
            }
          }
        : action
    ));

    // Close modal
    setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
  };

  // Load email templates for template selector
  const loadEmailTemplates = async () => {
    console.log('📧 Loading email templates...');
    console.log('📧 Selected business:', selectedBusiness?.id);
    
    if (!selectedBusiness?.id) {
      console.error('❌ No business selected');
      alert('No business selected. Please make sure you are logged in.');
      return;
    }
    
    setEmailTemplateSelector(prev => ({ ...prev, loading: true }));
    
    try {
      console.log('📧 Calling emailTemplateService.getTemplates...');
      const templates = await emailTemplateService.getTemplates(selectedBusiness.id);
      console.log('📧 Templates loaded:', templates?.length || 0);
      
      setEmailTemplateSelector(prev => ({ 
        ...prev, 
        templates: templates || [], 
        loading: false 
      }));
    } catch (error) {
      console.error('❌ Error loading email templates:', error);
      alert(`Failed to load email templates: ${error.message || 'Unknown error'}`);
      setEmailTemplateSelector(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle opening template selector
  const handleOpenTemplateSelector = () => {
    console.log('📧 Opening template selector...');
    console.log('📧 Business available:', !!selectedBusiness?.id);
    console.log('📧 Current emailTemplateSelector state:', emailTemplateSelector);
    
    setEmailTemplateSelector(prev => {
      console.log('📧 Setting emailTemplateSelector isOpen to true');
      return { ...prev, isOpen: true };
    });
    loadEmailTemplates();
  };

  // Handle selecting a template
  const handleSelectTemplate = (template: any) => {
    console.log('📧 Template selected:', template.name, 'ID:', template.id);
    
    // DON'T convert template to text - just store the template ID
    // The edge function will fetch and render the full template
    
    // Update email modal with template reference (not content)
    setEmailActionSettingsModal(prev => ({
      ...prev,
      subject: `[Template: ${template.name}]`, // Placeholder to show template is selected
      message: `Template "${template.name}" will be used with full design and styling.`, // Placeholder
      useTemplate: true,
      selectedTemplateId: template.id
    }));
    
    // Close template selector
    setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }));
  };

  // Handle saving Email message
  const handleSaveEmailMessage = () => {
    if (!emailActionSettingsModal.actionId) return;

    // Update the action with the new subject, message and recipient
    setActions(prev => prev.map(action => 
      action.id === emailActionSettingsModal.actionId
        ? { 
            ...action, 
            config: { 
              ...action.config, 
              subject: emailActionSettingsModal.subject,
              message: emailActionSettingsModal.message,
              recipient: emailActionSettingsModal.recipient,
              customEmail: emailActionSettingsModal.customEmail,
              useTemplate: emailActionSettingsModal.useTemplate,
              selectedTemplateId: emailActionSettingsModal.selectedTemplateId
            }
          }
        : action
    ));

    // Close modal
    setEmailActionSettingsModal({ 
      isOpen: false, 
      actionId: null, 
      subject: '', 
      message: '', 
      recipient: '', 
      customEmail: '',
      useTemplate: false,
      selectedTemplateId: null
    });
  };

  // Function to handle opening variables modal
  const handleOpenVariablesModal = (fieldName: string, currentValue: string) => {
    setVariablesModal({
      isOpen: true,
      currentField: fieldName,
      currentValue: currentValue
    });
  };

  // Function to handle closing variables modal
  const handleCloseVariablesModal = () => {
    setVariablesModal({
      isOpen: false,
      currentField: null,
      currentValue: ''
    });
  };

  // Function to insert variable into current field
  const insertVariableIntoField = (variable: string) => {
    if (!variablesModal.currentField) return;
    
    const fieldName = variablesModal.currentField;
    const currentValue = variablesModal.currentValue;
    
    // Insert the variable at the end of the current value
    const newValue = currentValue + variable;
    
    // Handle custom fields differently
    if (fieldName.startsWith('custom_fields.')) {
      const customFieldId = fieldName.replace('custom_fields.', '');
      setFieldMappings(prev => ({
        ...prev,
        custom_fields: {
          ...prev.custom_fields,
          [customFieldId]: newValue
        }
      }));
    } else {
      // Update regular field mapping
      setFieldMappings(prev => ({
        ...prev,
        [fieldName]: newValue
      }));
    }
    
    // Close the modal
    handleCloseVariablesModal();
  };

  // Get node position for wire rendering - using DOM-based positioning like drag wires
  const getNodePosition = (nodeId: string, portType: 'inflow' | 'outflow') => {
    if (!canvasRef.current) return { x: 0, y: 0 };

    // Find the actual DOM element for the port
    const portSelector = portType === 'inflow' 
      ? `[data-node-id="${nodeId}"][data-port-type="inflow"]`
      : `[data-node-id="${nodeId}"][data-port-type="outflow"]`;
    
    const portElement = canvasRef.current.querySelector(portSelector) as HTMLElement;
    
    if (portElement) {
      // Use the same calculation as drag wires - get actual DOM position
      const portRect = portElement.getBoundingClientRect();
      const canvasRect = canvasRef.current.getBoundingClientRect();
      
      return {
        x: portRect.left + portRect.width / 2 - canvasRect.left,
        y: portRect.top + portRect.height / 2 - canvasRect.top
      };
    }

    // Improved fallback calculation when DOM elements aren't available yet
    const trigger = triggers.find(t => t.id === nodeId);
    if (trigger) {
      // More precise calculation accounting for actual CSS structure:
      // p-4 = 32px padding, icon+text ≈ 48px, label = mt-1(4px) + py-1(8px) + text(16px) = 28px
      const basePadding = 32; // p-4 top+bottom padding  
      const contentHeight = 48; // Icon + name + type text
      const labelHeight = trigger.config?.uiLabel ? 32 : 0; // mt-1 + py-1 + px-2 + text + bg
      const totalHeight = basePadding + contentHeight + labelHeight;
      
      return {
        x: trigger.position.x + 100, // Center of node (min-w-[200px] / 2)
        y: trigger.position.y + totalHeight + 8 // Bottom + port radius
      };
    }
    
    const action = actions.find(a => a.id === nodeId);
    if (action) {
      // Same precise calculation for actions
      const basePadding = 32; // p-4 top+bottom padding
      const contentHeight = 48; // Icon + name + type text  
      const labelHeight = action.config?.uiLabel ? 32 : 0; // mt-1 + py-1 + px-2 + text + bg
      const totalHeight = basePadding + contentHeight + labelHeight;
      
      if (portType === 'inflow') {
        return {
          x: action.position.x + 100, // Center of node (min-w-[200px] / 2)
          y: action.position.y - 8    // Top - port radius
        };
      } else {
        return {
          x: action.position.x + 100, // Center of node (min-w-[200px] / 2)
          y: action.position.y + totalHeight + 8 // Bottom + port radius
        };
      }
    }
    
    return { x: 0, y: 0 };
  };

  const selectedTrigger = selectedTriggerId ? triggers.find(t => t.id === selectedTriggerId) : null;

  // Fetch lead forms when trigger settings modal opens and form type is lead_form
  useEffect(() => {
    if (isTriggerSettingsOpen && selectedTrigger?.type === 'form_submission' && selectedTrigger.config?.formType === 'lead_form') {
      fetchLeadForms();
    }
  }, [isTriggerSettingsOpen, selectedTrigger?.type, selectedTrigger?.config?.formType, selectedBusiness?.id]);

  // Update form fields when lead forms or triggers change (for CRM action settings)
  useEffect(() => {
    if (crmLeadActionSettingsModal.isOpen && leadForms.length > 0) {
      const formFields = getSelectedFormFields();
      setSelectedFormFields(formFields);
      console.log('🔄 Updated form fields due to data change:', formFields);
    }
  }, [leadForms, triggers, crmLeadActionSettingsModal.isOpen]);

  // Fetch lead forms when SMS action settings modal opens and there are form submission triggers connected
  useEffect(() => {
    if (smsActionSettingsModal.isOpen && smsActionSettingsModal.actionId) {
      // Check if this SMS action is connected to any form submission triggers
      const connectedTriggers = (() => {
        const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
          if (visited.has(nodeId)) return [];
          visited.add(nodeId);
          
          const directTrigger = triggers.find(trigger => trigger.id === nodeId);
          if (directTrigger) return [directTrigger];
          
          const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
          let foundTriggers: TriggerNodeType[] = [];
          
          for (const connection of incomingConnections) {
            const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
            foundTriggers = [...foundTriggers, ...triggersFromUpstream];
          }
          
          return foundTriggers;
        };
        
        return findTriggersInChain(smsActionSettingsModal.actionId);
      })();
      
      const hasFormSubmissionTrigger = connectedTriggers.some(trigger => 
        trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form'
      );
      
      if (hasFormSubmissionTrigger && leadForms.length === 0) {
        console.log('🔄 SMS action connected to form submission trigger, fetching lead forms...');
        fetchLeadForms();
      }
    }
  }, [smsActionSettingsModal.isOpen, smsActionSettingsModal.actionId, triggers, wireConnections, leadForms.length]);

  // Load custom field definitions when business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadCustomFieldDefinitions();
    }
  }, [selectedBusiness?.id]);

  const fetchLeadForms = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoadingLeadForms(true);
    try {
      const { data, error } = await supabase
        .from('lead_forms')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeadForms(data || []);
    } catch (error) {
      console.error('Error fetching lead forms:', error);
    } finally {
      setLoadingLeadForms(false);
    }
  };

  const fetchPipelines = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoadingPipelines(true);
    try {
      const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('pipeline_name', { ascending: true });

      if (error) throw error;
      setPipelines(data || []);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    } finally {
      setLoadingPipelines(false);
    }
  };

  // Load custom field definitions from database (same as CRM page)
  const loadCustomFieldDefinitions = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('opportunity_custom_fields')
        .select('id, field_name, field_type, display_order')
        .eq('business_id', selectedBusiness.id)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Convert database format to component format
      const definitions = (data || []).map(field => ({
        id: field.id,
        name: field.field_name,
        type: field.field_type
      }));

      setCustomFieldDefinitions(definitions);
    } catch (error) {
      console.error('Error loading custom field definitions:', error);
    }
  };

  // Load boards and columns for Create Task action
  const loadBoardsAndColumns = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Fetch all boards for the business
      const boards = await getBoards(selectedBusiness.id);
      
      // Update the modal state with boards
      setCreateTaskActionSettingsModal(prev => ({
        ...prev,
        boards: boards.map(board => ({
          id: board.id,
          board_title: board.board_title
        }))
      }));

      // If a board is selected, load its columns
      const currentBoardId = createTaskActionSettingsModal.selectedBoardId;
      if (currentBoardId) {
        const boardWithColumns = await getBoardWithColumns(currentBoardId);
        
        setCreateTaskActionSettingsModal(prev => ({
          ...prev,
          columns: boardWithColumns.columns.map(column => ({
            id: column.id,
            title: column.title
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading boards and columns:', error);
    }
  };

  // Function to load columns when a board is selected
  const loadColumnsForBoard = async (boardId: string) => {
    try {
      const boardWithColumns = await getBoardWithColumns(boardId);
      
      setCreateTaskActionSettingsModal(prev => ({
        ...prev,
        columns: boardWithColumns.columns.map(column => ({
          id: column.id,
          title: column.title
        }))
      }));
    } catch (error) {
      console.error('Error loading columns for board:', error);
    }
  };

  // Get selected form fields from the trigger configuration
  const getSelectedFormFields = (): Array<{id: string, type: string, label: string}> => {
    console.log('🔍 Getting selected form fields...');
    console.log('🔍 Available triggers:', triggers);
    console.log('🔍 Available lead forms:', leadForms);
    
    // Check each trigger to understand their structure
    triggers.forEach((trigger, index) => {
      console.log(`🔍 Trigger ${index}:`, {
        id: trigger.id,
        type: trigger.type,
        config: trigger.config
      });
    });
    
    // Find the form submission trigger connected to this action
    const formTrigger = triggers.find(t => {
      console.log('🔍 Checking trigger:', t.type, t.config);
      console.log('🔍 selectedForm value:', t.config?.selectedForm);
      console.log('🔍 selectedForm type:', typeof t.config?.selectedForm);
      console.log('🔍 selectedFormId:', t.config?.selectedFormId);
      console.log('🔍 formId:', t.config?.formId);
      
      // Check for various possible form ID properties
      const hasFormId = t.config?.selectedForm || t.config?.selectedFormId || t.config?.formId;
      console.log('🔍 hasFormId:', hasFormId);
      
      return t.type === 'form_submission' && 
             t.config?.formType === 'lead_form' && 
             hasFormId;
    });
    
    console.log('🔍 Found form trigger:', formTrigger);
    
    if (!formTrigger) {
      console.log('❌ No form submission trigger found');
      // Try a more lenient search
      const anyFormTrigger = triggers.find(t => t.type === 'form_submission');
      console.log('🔍 Any form submission trigger:', anyFormTrigger);
      
      // Check if we have any trigger with selectedForm
      const triggerWithForm = triggers.find(t => t.config?.selectedForm);
      console.log('🔍 Any trigger with selectedForm:', triggerWithForm);
      
      return [];
    }
    
    const selectedFormId = formTrigger.config?.selectedForm || formTrigger.config?.selectedFormId || formTrigger.config?.formId;
    console.log('🔍 Looking for form with ID:', selectedFormId);
    
    // Find the selected lead form
    const selectedForm = leadForms.find(form => {
      console.log('🔍 Comparing form:', form.id, 'with', selectedFormId);
      return form.id === selectedFormId;
    });
    console.log('🔍 Found selected form:', selectedForm);
    
    if (!selectedForm?.form_config?.fields) {
      console.log('❌ No form fields found in configuration');
      console.log('🔍 Form config:', selectedForm?.form_config);
      return [];
    }
    
    const formFields: Array<{id: string, type: string, label: string}> = [];
    
    // Process each field in the form
    selectedForm.form_config.fields.forEach((field: any) => {
      console.log('🔍 Processing field:', field);
      
      if (field.type === 'customer_info') {
        // Special handling for Customer Info component - create variables for standardized fields
        console.log('✅ Found Customer Info component, adding standardized fields');
        formFields.push(
          { id: `${field.id}_first_name`, type: 'text', label: 'First Name' },
          { id: `${field.id}_last_name`, type: 'text', label: 'Last Name' },
          { id: `${field.id}_email`, type: 'email', label: 'Email Address' },
          { id: `${field.id}_phone`, type: 'tel', label: 'Phone Number' }
        );
      } else {
        // Regular form field
        formFields.push({
          id: field.id,
          type: field.type,
          label: field.label
        });
      }
    });
    
    console.log('✅ Final form fields:', formFields);
    return formFields;
  };

  const loadWireConnections = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const data = await getAutomationConnections(selectedAutomation.id);
      setWireConnections(data);
    } catch (err) {
      console.error('Error loading wire connections:', err);
      setError('Failed to load wire connections');
    }
  };

  // Handle edit automation title
  const handleEditTitle = () => {
    if (selectedAutomation) {
      setEditTitle(selectedAutomation.name);
      setIsEditTitleModalOpen(true);
    }
  };

  const handleSaveTitle = async () => {
    if (!selectedAutomation?.id || !editTitle.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const updatedAutomation = await updateAutomation(selectedAutomation.id, {
        name: editTitle.trim()
      });

      // Update automations list
      setAutomations(prev => 
        prev.map(automation => 
          automation.id === selectedAutomation.id ? updatedAutomation : automation
        )
      );

      // Update selected automation
      setSelectedAutomation(updatedAutomation);
      setIsEditTitleModalOpen(false);
      setEditTitle('');
    } catch (err) {
      console.error('Error updating automation title:', err);
      setError('Failed to update automation title');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete automation
  const handleDeleteAutomation = () => {
    setIsDeleteAutomationModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAutomation?.id) return;

    try {
      setDeleteLoading(true);
      setError(null);

      await deleteAutomation(selectedAutomation.id);

      // Remove from automations list
      setAutomations(prev => prev.filter(automation => automation.id !== selectedAutomation.id));
      
      // Clear selected automation
      setSelectedAutomation(null);
      setIsDeleteAutomationModalOpen(false);
    } catch (err) {
      console.error('Error deleting automation:', err);
      setError('Failed to delete automation');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Render Linear Workflow View
  const renderLinearWorkflow = () => {
    // If no triggers exist, show initial + button to add first trigger
    if (triggers.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Your Workflow</h3>
            <p className="text-gray-600">Add your first trigger to begin</p>
          </div>
          <button
            onClick={handleAddTrigger}
            className="group relative bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white p-4 rounded-2xl shadow-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 border border-white/20"
          >
            <Plus className="h-8 w-8" />
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-3 py-1 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Add Trigger
            </div>
          </button>
        </div>
      );
    }

    // Build linear flow from triggers and their connected actions
    const linearFlow = buildLinearFlow();
    
    return (
      <div className="space-y-0">
        {linearFlow.map((flowItem, index) => (
          <div key={`flow-${index}`} id={`node-${flowItem.node?.id || 'add-' + index}`}>
            {flowItem.type === 'trigger' && flowItem.node && (
              <>
                {renderLinearTrigger(flowItem.node as TriggerNodeType)}
                {/* Show connection line if this trigger has outgoing connections */}
                {wireConnections.some(conn => conn.fromNodeId === flowItem.node!.id) && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-6 bg-green-500"></div>
                  </div>
                )}
              </>
            )}
            {flowItem.type === 'action' && flowItem.node && (
              <>
                {renderLinearAction(flowItem.node as ActionNodeType, flowItem.level)}
                {/* Show connection line if this action has outgoing connections */}
                {wireConnections.some(conn => conn.fromNodeId === flowItem.node!.id) && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-6 bg-green-500"></div>
                  </div>
                )}
              </>
            )}
            {flowItem.type === 'add-action' && flowItem.parentId && (
              <div className="py-4">
                {renderAddActionButton(flowItem.parentId, flowItem.level)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Build linear flow structure from existing triggers and actions
  const buildLinearFlow = () => {
    const flow: Array<{
      type: 'trigger' | 'action' | 'add-action';
      node?: TriggerNodeType | ActionNodeType;
      parentId?: string;
      level: number;
    }> = [];

    // Get all connected action IDs
    const connectedActionIds = new Set(wireConnections.map(conn => conn.toNodeId));
    
    // Start with triggers (should typically be one, but handle multiple)
    triggers.forEach(trigger => {
      flow.push({ type: 'trigger', node: trigger, level: 0 });
      
      // Build the complete action chain starting from this trigger
      const actionChain = buildActionChain(trigger.id);
      
      // Add all actions in the chain
      actionChain.forEach((action, index) => {
        flow.push({ type: 'action', node: action, level: 1 });
      });
      
      // Only add + button if this is the end of the chain (no actions connect FROM the last action)
      const lastNodeId = actionChain.length > 0 ? actionChain[actionChain.length - 1].id : trigger.id;
      const hasOutgoingConnection = wireConnections.some(conn => conn.fromNodeId === lastNodeId);
      
      if (!hasOutgoingConnection) {
        flow.push({ type: 'add-action', parentId: lastNodeId, level: 1 });
      }
    });

    // Add unconnected actions at the end
    const unconnectedActions = actions.filter(action => !connectedActionIds.has(action.id));
    
    unconnectedActions.forEach(action => {
      flow.push({ type: 'action', node: action, level: 0 });
      // Add + button under each unconnected action since they have no outgoing connections
      flow.push({ type: 'add-action', parentId: action.id, level: 0 });
    });

    return flow;
  };

  // Build complete action chain following wire connections
  const buildActionChain = (startNodeId: string): ActionNodeType[] => {
    const chain: ActionNodeType[] = [];
    const visited = new Set<string>();
    
    const traverseChain = (nodeId: string) => {
      if (visited.has(nodeId)) return; // Prevent infinite loops
      visited.add(nodeId);
      
      // Find actions connected to this node
      const connections = wireConnections.filter(conn => conn.fromNodeId === nodeId);
      
      connections.forEach(connection => {
        const action = actions.find(a => a.id === connection.toNodeId);
        if (action) {
          chain.push(action);
          // Continue traversing from this action
          traverseChain(action.id);
        }
      });
    };
    
    traverseChain(startNodeId);
    return chain;
  };


  // Get actions connected to a specific node (trigger or action)
  const getActionsConnectedToNode = (nodeId: string): ActionNodeType[] => {
    const directConnections = wireConnections.filter(conn => conn.fromNodeId === nodeId);
    const connectedActions: ActionNodeType[] = [];
    
    directConnections.forEach(connection => {
      const action = actions.find(a => a.id === connection.toNodeId);
      if (action) {
        connectedActions.push(action);
      }
    });
    
    return connectedActions;
  };

  // Render trigger in linear layout (matching existing design)
  const renderLinearTrigger = (trigger: TriggerNodeType) => {
    const formatEventType = (eventType: string, triggerType?: string) => {
      if (triggerType === 'form_submission') {
        return 'Form Submitted';
      }
      return eventType.charAt(0).toUpperCase() + eventType.slice(1);
    };

    return (
      <div className="flex justify-center">
        <div 
          className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-4 min-w-[320px] max-w-[400px] w-full relative cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleTriggerSettings(trigger.id);
          }}
          title="Configure trigger"
        >

          <div className="flex items-center gap-4">
            {/* Icon/Avatar */}
            <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Zap className="h-6 w-6 text-white" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate">{trigger.name}</h3>
              </div>
              <p className="text-sm text-gray-600">{formatEventType(trigger.config?.eventType || 'created', trigger.type)}</p>
              {trigger.config?.uiLabel && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                    {trigger.config.uiLabel}
                  </span>
                </div>
              )}
            </div>
            
            {/* Status/Info */}
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-medium text-purple-600">Trigger</div>
              <div className="text-xs text-gray-500 mt-1">Active</div>
            </div>
          </div>

          {/* Outflow Port */}
          <LinearConnectionPort nodeId={trigger.id} type="outflow" />
        </div>
      </div>
    );
  };

  // Render action in linear layout (matching existing design)
  const renderLinearAction = (action: ActionNodeType, level: number) => {
    const getActionIcon = (actionType: string) => {
      switch (actionType) {
        case 'sms':
          return <MessageCircle className="h-5 w-5 text-white" />;
        case 'email':
          return <Mail className="h-5 w-5 text-white" />;
        case 'delay':
          return <Clock className="h-5 w-5 text-white" />;
        case 'create_crm_lead':
          return <Plus className="h-5 w-5 text-white" />;
        case 'check':
          return <Eye className="h-5 w-5 text-white" />;
        case 'create_task':
          return <CheckSquare className="h-5 w-5 text-white" />;
        default:
          return <Zap className="h-5 w-5 text-white" />;
      }
    };
    
    const getActionColor = (actionType: string) => {
      switch (actionType) {
        case 'sms':
          return 'bg-green-600';
        case 'email':
          return 'bg-blue-600';
        case 'delay':
          return 'bg-orange-600';
        case 'create_crm_lead':
          return 'bg-purple-600';
        case 'check':
          return 'bg-yellow-600';
        case 'create_task':
          return 'bg-indigo-600';
        default:
          return 'bg-gray-600';
      }
    };

    return (
      <div className="flex justify-center">
        {/* Action card - booking card style */}
        <div 
          className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-4 min-w-[320px] max-w-[400px] w-full relative cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleActionSettingsClick(action.id);
          }}
          title="Configure action"
        >

          {/* Inflow Port */}
          <LinearConnectionPort nodeId={action.id} type="inflow" />

          <div className="flex items-center gap-4">
            {/* Icon/Avatar */}
            <div className={`w-12 h-12 ${getActionColor(action.type)} rounded-full flex items-center justify-center flex-shrink-0`}>
              <div className="text-white">
                {getActionIcon(action.type)}
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate">{action.name}</h3>
              </div>
              <p className="text-sm text-gray-600 capitalize">{action.type.replace('_', ' ')} Action</p>
              {action.config?.uiLabel && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                    {action.config.uiLabel}
                  </span>
                </div>
              )}
            </div>
            
            {/* Status/Info */}
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-medium text-gray-900">Action</div>
              <div className="text-xs text-gray-500 mt-1">Ready</div>
            </div>
          </div>

          {/* Outflow Port */}
          <LinearConnectionPort nodeId={action.id} type="outflow" />
        </div>
      </div>
    );
  };

  // Render add action button
  const renderAddActionButton = (parentId: string, level: number) => {
    return (
      <div className="flex justify-center">
        <button
          onClick={() => handleAddActionSequential(parentId)}
          className="group relative bg-gray-100 hover:bg-gray-200 text-gray-600 p-3 rounded-full shadow-sm transition-all duration-200 hover:shadow-md"
          title="Add Action"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    );
  };

  // Handle adding action in sequence (below current action/trigger)
  const handleAddActionSequential = (parentId: string) => {
    // Set the parent ID so the new action will be automatically connected
    setPendingActionParent(parentId);
    handleAddAction();
  };

  // Handle adding parallel actions (left/right of current action)
  const handleAddActionParallel = (actionId: string, side: 'left' | 'right') => {
    // Find the parent of the current action to connect the new action to the same parent
    const currentActionConnection = wireConnections.find(conn => conn.toNodeId === actionId);
    if (currentActionConnection) {
      setPendingActionParent(currentActionConnection.fromNodeId);
      handleAddAction();
    } else {
      console.log('No parent found for parallel action');
    }
  };

  // Handle deleting action from linear view
  const handleDeleteActionLinear = (actionId: string) => {
    // Remove action from state
    setActions(prev => prev.filter(action => action.id !== actionId));
    
    // Remove any wire connections involving this action
    setWireConnections(prev => prev.filter(conn => 
      conn.fromNodeId !== actionId && conn.toNodeId !== actionId
    ));
  };

  // Linear Connection Port Component
  const LinearConnectionPort: React.FC<{
    nodeId: string;
    type: 'inflow' | 'outflow';
  }> = ({ nodeId, type }) => {
    const isSelected = selectedOutflowPort === nodeId && type === 'outflow';
    
    // Check if this port is connected
    const isConnected = type === 'inflow' 
      ? wireConnections.some(conn => conn.toNodeId === nodeId)
      : wireConnections.some(conn => conn.fromNodeId === nodeId);
    
    const handlePortClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // If the port is connected, show disconnect popup
      if (isConnected) {
        const rect = e.currentTarget.getBoundingClientRect();
        setDisconnectPopup({
          show: true,
          nodeId,
          portType: type,
          position: {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          }
        });
        return;
      }
      
      // Original connection logic for unconnected ports
      if (type === 'outflow') {
        // If this port is already selected, deselect it
        if (isSelected) {
          setSelectedOutflowPort(null);
        } else {
          // Select this outflow port (turn it red)
          setSelectedOutflowPort(nodeId);
        }
      } else if (type === 'inflow' && selectedOutflowPort) {
        // Connect the selected outflow to this inflow
        handleLinearConnection(selectedOutflowPort, nodeId);
        setSelectedOutflowPort(null); // Clear selection
      }
    };
    
    return (
      <div
        className={`absolute w-4 h-4 rounded-full border-2 shadow-lg hover:scale-110 transition-all cursor-pointer ${
          type === 'inflow' 
            ? isConnected
              ? 'bg-green-500 border-white top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hover:bg-green-600'
              : 'bg-white border-gray-500 top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hover:bg-gray-50'
            : isSelected
              ? 'bg-red-500 border-white hover:bg-red-600 bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2'
              : isConnected
                ? 'bg-green-500 border-white hover:bg-green-600 bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2'
                : 'bg-gray-500 border-white hover:bg-gray-600 bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2'
        }`}
        onClick={handlePortClick}
        title={
          isConnected 
            ? `Connected ${type} - click to disconnect`
            : type === 'inflow' ? 'Input connection' : 'Output connection'
        }
      />
    );
  };

  // Handle creating connection in linear view
  const handleLinearConnection = (fromNodeId: string, toNodeId: string) => {
    // Check if connection already exists
    const existingConnection = wireConnections.find(
      conn => conn.fromNodeId === fromNodeId && conn.toNodeId === toNodeId
    );
    
    if (existingConnection) {
      console.log('Connection already exists');
      return;
    }
    
    // Create new connection
    const newConnection: WireConnection = {
      id: `wire-${Date.now()}`,
      fromNodeId,
      toNodeId,
      fromPort: 'outflow',
      toPort: 'inflow'
    };
    
    setWireConnections(prev => [...prev, newConnection]);
    console.log(`🔗 Connected ${fromNodeId} → ${toNodeId}`);
  };

  // Handle disconnecting in linear view
  const handleLinearDisconnect = (nodeId: string, portType: 'inflow' | 'outflow') => {
    if (portType === 'inflow') {
      // Remove connection where this node is the target
      setWireConnections(prev => prev.filter(conn => conn.toNodeId !== nodeId));
    } else {
      // Remove connection where this node is the source
      setWireConnections(prev => prev.filter(conn => conn.fromNodeId !== nodeId));
    }
    
    setDisconnectPopup(null);
    console.log(`🔗 Disconnected ${nodeId} (${portType})`);
  };

  // Render main content based on device type
  const renderMainContent = () => {
  if (isMobile) {
    return (
        <div 
          className="min-h-screen bg-gray-800" 
          data-automations-page
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}>
          
          {selectedAutomation ? (
            /* Mobile Automation Detail View */
            <div className="flex flex-col h-screen">
              {/* Mobile Header with Back Button */}
              <div className="bg-gray-800 border-b border-gray-700 px-4 py-4 sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedAutomation(null)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5 text-gray-300" />
                  </button>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-white truncate">{selectedAutomation.name}</h2>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${selectedAutomation.is_live ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-400">{selectedAutomation.is_live ? 'Live' : 'Draft'}</span>
          </div>
            </div>
                  <button
                    onClick={handleToggleLive}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      selectedAutomation.is_live
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {selectedAutomation.is_live ? 'Turn Off' : 'Go Live'}
                  </button>
          </div>
              </div>

              {/* Mobile Tab Navigation */}
              <div className="bg-gray-800 border-b border-gray-700 px-4">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('workflow-new')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'workflow-new'
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400'
                    }`}
                  >
                    Workflow
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'history'
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400'
                    }`}
                  >
                    History
                  </button>
                </div>
              </div>

              {/* Mobile Content Area */}
              <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 overflow-y-auto">
                {activeTab === 'workflow-new' ? (
                  <div className="p-4">
                    {renderLinearWorkflow()}
                  </div>
                ) : (
                  <div className="p-4">
                    <div className="text-center py-12">
                      <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Execution History</h3>
                      <p className="text-gray-600">View automation execution history here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Mobile Menu View */
            <div className="flex flex-col h-screen">
              {/* Mobile Menu Header */}
              <div className="p-6 border-b border-gray-700">
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-white">Automations</h2>
                  <p className="text-sm text-gray-300">Workflow Management</p>
                </div>
                <button
                  onClick={handleCreateClick}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg"
                >
                  <Plus className="h-4 w-4" />
                  Create
                </button>
              </div>

              {/* Mobile Automation List */}
              <div className="flex-1 overflow-y-auto p-4">
                {loading && automations.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-blue-600/30"></div>
                    <p className="mt-4 text-base text-gray-300 font-medium">Loading automations...</p>
                  </div>
                ) : automations.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-700/20 flex items-center justify-center mx-auto mb-4">
                      <Zap className="h-8 w-8 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">No automations yet</h3>
                    <p className="text-gray-400 leading-relaxed">
                      Create your first automation to get started.
          </p>
        </div>
                ) : (
                  <div className="space-y-3">
                    {automations.map((automation) => (
                      <button
                        key={automation.id}
                        onClick={() => handleAutomationClick(automation)}
                        className="w-full text-left p-4 rounded-xl bg-gray-700 hover:bg-gray-600 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                              {automation.name}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              automation.is_live ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    );
  }

    // Desktop view
  return (
      <div 
        className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" 
        data-automations-page
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Left Sidebar */}
      <div className="w-80 bg-gray-800 border-r border-gray-700 flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-white">Automations</h2>
            <p className="text-sm text-gray-300">Workflow Management</p>
          </div>
          <button
            onClick={handleCreateClick}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg"
          >
            <Plus className="h-4 w-4" />
            Create
          </button>
        </div>

        {/* Automation List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && automations.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-purple-600/30"></div>
              <p className="mt-4 text-base text-gray-300 font-medium">Loading automations...</p>
            </div>
          ) : automations.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600/20 to-indigo-600/20 flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No automations yet</h3>
              <p className="text-gray-400 leading-relaxed">
                Create your first automation to get started.
              </p>
            </div>
          ) : (
            <nav className="space-y-2">
              {automations.map((automation) => {
                const isActive = selectedAutomation?.id === automation.id;
                return (
                  <button
                  key={automation.id}
                  onClick={() => handleAutomationClick(automation)}
                    className={`w-full text-left p-3 rounded-xl transition-all duration-200 group ${
                      isActive
                        ? 'bg-gray-700 text-white shadow-sm'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${
                          isActive ? 'text-white' : 'text-gray-300 group-hover:text-white'
                        }`}>
                        {automation.name}
                    </div>
                      </div>
                      <div className={`w-3 h-3 rounded-full ml-2 ${
                        automation.is_live 
                          ? 'bg-green-500' 
                          : 'bg-red-500'
                      }`}></div>
                      </div>
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {selectedAutomation ? (
          <>
            {/* Header with automation info and tabs */}
            <div className="bg-gray-800 border-b border-gray-700 px-4 md:px-8 py-4 sticky top-0 z-30">
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-white">
                          {selectedAutomation.name}
                        </h2>
                        <button
                          onClick={handleEditTitle}
                          className="text-gray-400 hover:text-blue-400 transition-all duration-200 p-1.5 hover:bg-gray-700 rounded-xl"
                          title="Edit title"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      </div>

                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleToggleLive}
                      className={`px-4 py-2.5 rounded-2xl font-semibold transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm ${
                        selectedAutomation.is_live
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {selectedAutomation.is_live ? 'Turn Off' : 'Go Live'}
                    </button>
                    <button
                      onClick={handleDeleteAutomation}
                      className="p-2.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-2xl transition-all duration-200 transform hover:scale-105 active:scale-95"
                      title="Delete automation"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-700">
                <button
                  onClick={() => setActiveTab('workflow-new')}
                  className={`px-6 py-3 font-semibold text-sm border-b-2 transition-all duration-300 ease-out transform hover:scale-105 ${
                    activeTab === 'workflow-new'
                      ? 'border-blue-500 text-blue-400 bg-gray-700/50'
                      : 'border-transparent text-gray-400 hover:text-blue-400 hover:bg-gray-700/30'
                  }`}
                >
                  <Zap className="h-4 w-4 inline mr-2" />
                  Workflow
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-6 py-3 font-semibold text-sm border-b-2 transition-all duration-300 ease-out transform hover:scale-105 ${
                    activeTab === 'history'
                      ? 'border-blue-500 text-blue-400 bg-gray-700/50'
                      : 'border-transparent text-gray-400 hover:text-blue-400 hover:bg-gray-700/30'
                  }`}
                >
                  <History className="h-4 w-4 inline mr-2" />
                  History
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'workflow' ? (
              /* Workflow Canvas */
              <div className="flex-1 relative bg-gradient-to-br from-gray-50/50 to-white/50">
                {/* Canvas Controls */}
                <div className="absolute top-6 left-6 z-10">
                  <button
                    onClick={handleAddTrigger}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2.5 rounded-2xl font-semibold flex items-center gap-2 shadow-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/20"
                  >
                    <Plus className="h-4 w-4" />
                    Add Trigger
                  </button>
                </div>

                <div className="absolute top-6 left-48 z-10">
                  <button
                    onClick={handleAddAction}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2.5 rounded-2xl font-semibold flex items-center gap-2 shadow-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/20"
                  >
                    <Plus className="h-4 w-4" />
                    Add Action
                  </button>
                </div>

                {/* Dotted Canvas */}
                <div 
                  ref={canvasRef}
                  data-canvas
                  className="w-full h-full relative overflow-hidden"
                  style={{
                    backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                >
                  {/* Trigger Nodes */}
                  {triggers.map((trigger) => (
                    <TriggerNode
                      key={trigger.id}
                      trigger={trigger}
                      onMouseDown={handleMouseDown}
                      onStartWireDrag={handleStartWireDrag}
                      onSettingsClick={handleTriggerSettings}
                      onWireDropped={handleWireDropped}
                    />
                  ))}

                  {/* Action Nodes */}
                  {actions.map((action) => (
                    <ActionNode
                      key={action.id}
                      action={action}
                      onMouseDown={handleActionMouseDown}
                      onStartWireDrag={handleStartWireDrag}
                      onSettingsClick={handleActionSettingsClick}
                      onWireDropped={handleWireDropped}
                    />
                  ))}

                  {/* Wire being dragged */}
                  {wireState.isDragging && (
                    <svg 
                      className="absolute inset-0 pointer-events-none" 
                      style={{ 
                        zIndex: 1000,
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                          fill="#6b7280"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" />
                        </marker>
                      </defs>
                      
                      {/* Connection wire */}
                      <line
                        x1={wireState.startPosition.x}
                        y1={wireState.startPosition.y}
                        x2={wireState.currentPosition.x || wireState.startPosition.x}
                        y2={wireState.currentPosition.y || wireState.startPosition.y}
                        stroke="#6b7280"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                        opacity="0.8"
                      />
                      
                      {/* Start point indicator */}
                      <circle
                        cx={wireState.startPosition.x}
                        cy={wireState.startPosition.y}
                        r="4"
                        fill="#6b7280"
                        opacity="0.9"
                      />
                      
                      {/* End point indicator */}
                      {wireState.currentPosition.x && wireState.currentPosition.y && (
                        <circle
                          cx={wireState.currentPosition.x}
                          cy={wireState.currentPosition.y}
                          r="3"
                          fill="#ffffff"
                          stroke="#6b7280"
                          strokeWidth="2"
                          opacity="0.9"
                        />
                      )}
                    </svg>
                  )}

                  {/* Permanent wire connections */}
                  {wireConnections.length > 0 && (
                    <svg 
                      className="absolute inset-0 pointer-events-none" 
                      style={{ 
                        zIndex: 500,
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <defs>
                        <marker
                          id="permanent-arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                          fill="#6b7280"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" />
                        </marker>
                      </defs>
                      
                      {wireConnections.map((connection) => {
                        const fromPos = getNodePosition(connection.fromNodeId, 'outflow');
                        const toPos = getNodePosition(connection.toNodeId, 'inflow');
                        
                        return (
                          <g key={connection.id}>
                            {/* Connection line */}
                            <line
                              x1={fromPos.x}
                              y1={fromPos.y}
                              x2={toPos.x}
                              y2={toPos.y}
                              stroke="#6b7280"
                              strokeWidth="3"
                              opacity="0.8"
                            />
                            
                            {/* Connection points */}
                            <circle
                              cx={fromPos.x}
                              cy={fromPos.y}
                              r="3"
                              fill="#6b7280"
                              opacity="0.9"
                            />
                            <circle
                              cx={toPos.x}
                              cy={toPos.y}
                              r="3"
                              fill="#6b7280"
                              opacity="0.9"
                            />
                          </g>
                        );
                      })}
                    </svg>
                  )}

                  {/* Render nodes */}
                </div>
              </div>
            ) : activeTab === 'workflow-new' ? (
              /* Workflow New View - Linear Layout */
              <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 overflow-y-auto relative p-4 md:p-6">
                <div className="max-w-4xl mx-auto p-6">
                  {/* Linear Workflow Container */}
                  <div className="space-y-6">
                    {/* Render Linear Workflow */}
                    {renderLinearWorkflow()}
                  </div>
                </div>

                {/* Disconnect Popup */}
                {disconnectPopup && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-50" 
                      onClick={() => setDisconnectPopup(null)}
                    />
                    
                    {/* Popup */}
                    <div
                      className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-xl p-3"
                      style={{
                        left: disconnectPopup.position.x - 50,
                        top: disconnectPopup.position.y - 40,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <button
                        onClick={() => handleLinearDisconnect(disconnectPopup.nodeId, disconnectPopup.portType)}
                        className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors text-sm font-medium"
                      >
                        <X className="h-4 w-4" />
                        Disconnect
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Execution History */
              <div className="flex-1 bg-gradient-to-br from-gray-50/50 to-white/50 p-6">
                {executionSummary && (
                  /* Summary Stats */
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105" style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}>
                      <div className="text-2xl font-bold text-blue-600">
                        {executionSummary.running_executions}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Running</div>
                    </div>
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl border border-green-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105" style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}>
                      <div className="text-2xl font-bold text-green-600">
                        {executionSummary.successful_executions}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Successful</div>
                    </div>
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl border border-red-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105" style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}>
                      <div className="text-2xl font-bold text-red-600">
                        {executionSummary.failed_executions}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Failed</div>
                    </div>

                  </div>
                )}

                {/* Execution List */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-medium text-gray-900">Recent Executions</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {executions.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        No execution history yet.
                        <br />
                        Executions will appear here when the automation runs.
                      </div>
                    ) : (
                      executions.map((execution) => {
                        const actualStatus = getActualExecutionStatus(execution);
                        return (
                          <div key={execution.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(actualStatus)}
                                <div>
                                  <div className="font-medium text-gray-900 capitalize">
                                    {actualStatus}
                                  </div>
                                <div className="text-sm text-gray-500">
                                  {formatRelativeTime(execution.started_at)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-sm text-gray-900">
                                  {execution.executed_actions}/{execution.total_actions} actions
                                </div>
                                {execution.completed_at && (
                                  <div className="text-sm text-gray-500">
                                    {formatDuration(
                                      new Date(execution.completed_at).getTime() - 
                                      new Date(execution.started_at).getTime()
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleViewExecution(execution.id)}
                                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                              >
                                <Eye className="h-3 w-3" />
                                Details
                              </button>
                            </div>
                          </div>
                          {execution.error_message && (
                            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                              {execution.error_message}
                            </div>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Zap className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                No automation selected
              </h3>
              <p className="text-gray-600 leading-relaxed max-w-md">
                Choose an automation from the sidebar to view and edit its workflow.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-700 hover:text-red-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}




      {/* Execution Details Modal */}
      {isExecutionDetailsOpen && selectedExecutionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 h-5/6">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Execution Details</h2>
              <button
                onClick={handleCloseExecutionDetails}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 h-full overflow-y-auto">
              {loadingSteps ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading execution details...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Execution Overview */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Execution Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            const actualStatus = execution ? getActualExecutionStatus(execution) : 'pending';
                            return (
                              <>
                                {getStatusIcon(actualStatus)}
                                <span className="font-medium capitalize">
                                  {actualStatus}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Started</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.started_at && 
                            new Date(executions.find(e => e.id.toString() === selectedExecutionId)!.started_at).toLocaleString()
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Duration</div>
                        <div className="font-medium mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            if (execution?.completed_at) {
                              return formatDuration(
                                new Date(execution.completed_at).getTime() - 
                                new Date(execution.started_at).getTime()
                              );
                            }
                            return 'Running...';
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Actions</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.executed_actions || 0}/
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.total_actions || 0}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Context Data */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Booking Information</h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      {(() => {
                        const execution = executions.find(e => e.id === selectedExecutionId);
                        const contextData = execution?.context_data;
                        const bookingData = contextData?.booking_data;

                        if (!bookingData) {
                          return (
                            <div className="text-gray-500 text-center py-4">
                              No booking data available for this execution
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4">
                            {/* Basic Booking Info */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-sm text-gray-500">Booking ID</div>
                                <div className="font-medium">{bookingData.booking_id || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Title</div>
                                <div className="font-medium">{bookingData.title || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Grand Total</div>
                                <div className="font-medium">${bookingData.grand_total || 0}</div>
                              </div>
                            </div>

                            {/* Customer Info */}
                            {bookingData.customer && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Customer</div>
                                <div className="bg-gray-50 rounded p-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500">Name</div>
                                    <div className="text-sm">{bookingData.customer.full_name || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Email</div>
                                    <div className="text-sm">{bookingData.customer.email || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Phone</div>
                                    <div className="text-sm">{bookingData.customer.phone || 'N/A'}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Schedule Info */}
                            {bookingData.schedule && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Schedule</div>
                                <div className="bg-gray-50 rounded p-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500">Drop Off</div>
                                    <div className="text-sm">
                                      {bookingData.schedule.drop_off_date || 'N/A'} {bookingData.schedule.drop_off_time || ''}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Start</div>
                                    <div className="text-sm">
                                      {bookingData.schedule.start_date || 'N/A'} {bookingData.schedule.start_time || ''}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Services */}
                            {bookingData.services && bookingData.services.length > 0 && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Services</div>
                                <div className="space-y-2">
                                  {bookingData.services.map((service: any, index: number) => (
                                    <div key={index} className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="font-medium text-sm">{service.name}</div>
                                          <div className="text-xs text-gray-500">
                                            {service.vehicle?.year} {service.vehicle?.make} {service.vehicle?.model}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium">${service.price}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Raw Context Data (collapsible) */}
                            <details className="border-t pt-4">
                              <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                                Raw Context Data (Advanced)
                              </summary>
                              <div className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-64">
                                <pre>{JSON.stringify(contextData, null, 2)}</pre>
                              </div>
                            </details>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Automation Flow Progress */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">Automation Flow Progress</h3>
                    {(() => {
                      const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                      if (!execution) return null;

                      // Get all automation actions in order (using position.y as order since ActionNode doesn't have order)
                      const allActions = [...actions].sort((a, b) => a.position.y - b.position.y);
                      
                      if (allActions.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            No automation steps configured.
                          </div>
                        );
                      }

                      // Create a map of execution steps by action_id for quick lookup
                      const executionStepMap = new Map();
                      executionSteps.forEach(step => {
                        if (step.action_id) {
                          executionStepMap.set(step.action_id, step);
                        }
                      });

                      return (
                        <div className="space-y-4">
                          {/* Progress Bar */}
                          <div className="bg-gray-200 rounded-full h-2 mb-6">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{ 
                                width: `${Math.max(5, (execution.executed_actions / Math.max(execution.total_actions, 1)) * 100)}%` 
                              }}
                            ></div>
                          </div>

                          {/* Step Flow */}
                          <div className="relative">
                            {allActions.map((action, index) => {
                              const executionStep = executionStepMap.get(action.id);
                              const isExecuted = !!executionStep;
                              const isCurrentStep = index === execution.executed_actions && !isExecuted;

                              
                              // Determine step status
                              let stepStatus = 'pending';
                              let stepIcon = <Clock className="h-4 w-4 text-gray-400" />;
                              let stepBgColor = 'bg-gray-100';
                              let stepBorderColor = 'border-gray-200';
                              let stepTextColor = 'text-gray-600';
                              
                              if (executionStep) {
                                stepStatus = executionStep.execution_status;
                                if (stepStatus === 'completed') {
                                  stepIcon = <CheckCircle className="h-4 w-4 text-green-600" />;
                                  stepBgColor = 'bg-green-50';
                                  stepBorderColor = 'border-green-200';
                                  stepTextColor = 'text-green-800';
                                } else if (stepStatus === 'failed') {
                                  stepIcon = <XCircle className="h-4 w-4 text-red-600" />;
                                  stepBgColor = 'bg-red-50';
                                  stepBorderColor = 'border-red-200';
                                  stepTextColor = 'text-red-800';
                                } else if (stepStatus === 'running') {
                                  stepIcon = <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
                                  stepBgColor = 'bg-blue-50';
                                  stepBorderColor = 'border-blue-200';
                                  stepTextColor = 'text-blue-800';
                                }
                              } else if (isCurrentStep) {
                                stepStatus = 'current';
                                stepIcon = <Clock className="h-4 w-4 text-blue-600 animate-pulse" />;
                                stepBgColor = 'bg-blue-50';
                                stepBorderColor = 'border-blue-300';
                                stepTextColor = 'text-blue-800';
                              }

                              return (
                                <div key={action.id} className="relative">
                                  {/* Connector Line */}
                                  {index < allActions.length - 1 && (
                                    <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200"></div>
                                  )}
                                  
                                  <div className={`relative flex items-start gap-4 p-4 rounded-lg border ${stepBorderColor} ${stepBgColor} transition-all duration-300`}>
                                    {/* Step Number & Icon */}
                                    <div className="flex-shrink-0 flex flex-col items-center">
                                      <div className={`w-8 h-8 rounded-full border-2 ${stepBorderColor} ${stepBgColor} flex items-center justify-center font-medium text-sm ${stepTextColor}`}>
                                        {isExecuted || isCurrentStep ? stepIcon : (index + 1)}
                                      </div>
                                    </div>

                                    {/* Step Content */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-2">
                                        <div>
                                          <h4 className={`font-medium ${stepTextColor}`}>
                                            {action.type === 'email' && '📧 Send Email'}
                                            {action.type === 'sms' && '💬 Send SMS'}
                                            {action.type === 'wait' && '⏱️ Wait'}
                                            {action.type === 'webhook' && '🔗 Webhook'}
                                            {action.type === 'update_booking' && '📝 Update Booking'}
                                            {action.type === 'create_lead' && '🎯 Create Lead'}
                                            {action.type === 'conditional' && '🔀 Conditional'}
                                            {!['email', 'sms', 'wait', 'webhook', 'update_booking', 'create_lead', 'conditional'].includes(action.type) && 
                                              `${action.type.charAt(0).toUpperCase() + action.type.slice(1)} Action`
                                            }
                                          </h4>
                                          <div className="text-sm text-gray-500 space-y-1">
                                            {action.type === 'email' && (
                                              <div>
                                                <div>To: {action.config?.recipient || 'Customer'}</div>
                                                {action.config?.subject && <div>Subject: {action.config.subject}</div>}
                                              </div>
                                            )}
                                            {action.type === 'sms' && (
                                              <div>
                                                <div>To: {action.config?.recipient || 'Customer'}</div>
                                                {action.config?.message && (
                                                  <div className="truncate max-w-xs">Message: "{action.config.message}"</div>
                                                )}
                                                {executionStep?.result_data?.scheduled_for && (
                                                  <div className="text-blue-600 font-medium">
                                                    📅 Scheduled: {new Date(executionStep.result_data.scheduled_for).toLocaleString()}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {action.type === 'wait' && (
                                              <div>
                                                <div>Duration: {action.config?.duration || '1'} {action.config?.unit || 'minutes'}</div>
                                                {executionStep?.started_at && (
                                                  <div className="text-blue-600 font-medium">
                                                    ⏰ Completes: {new Date(
                                                      new Date(executionStep.started_at).getTime() + 
                                                      (parseInt(action.config?.duration || '1') * 
                                                       (action.config?.unit === 'hours' ? 3600000 : 
                                                        action.config?.unit === 'days' ? 86400000 : 60000))
                                                    ).toLocaleString()}
                                                  </div>
                                                )}
                                                {!executionStep && stepStatus === 'current' && (
                                                  <div className="text-orange-600 font-medium">
                                                    ⏳ Will start after previous step completes
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {action.type === 'webhook' && (
                                              <div>
                                                <div>URL: {action.config?.url || 'Not configured'}</div>
                                                {action.config?.method && <div>Method: {action.config.method}</div>}
                                              </div>
                                            )}
                                            {action.type === 'update_booking' && (
                                              <div>
                                                <div>Update booking properties</div>
                                                {action.config?.status && <div>Status: {action.config.status}</div>}
                                              </div>
                                            )}
                                            {action.type === 'create_lead' && (
                                              <div>
                                                <div>Create new lead</div>
                                                {action.config?.pipeline && <div>Pipeline: {action.config.pipeline}</div>}
                                              </div>
                                            )}
                                            {action.type === 'conditional' && (
                                              <div>
                                                <div>Conditional logic</div>
                                                {action.config?.condition && <div>Condition: {action.config.condition}</div>}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="flex flex-col items-end gap-1">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                              stepStatus === 'completed' ? 'bg-green-100 text-green-800' :
                                              stepStatus === 'failed' ? 'bg-red-100 text-red-800' :
                                              stepStatus === 'running' || stepStatus === 'current' ? 'bg-blue-100 text-blue-800' :
                                              'bg-gray-100 text-gray-600'
                                            }`}>
                                              {stepStatus === 'current' ? 'Next' : stepStatus.charAt(0).toUpperCase() + stepStatus.slice(1)}
                                            </span>
                                            {executionStep?.started_at && (
                                              <span className="text-xs text-gray-500">
                                                Started {formatRelativeTime(executionStep.started_at)}
                                              </span>
                                            )}
                                            {executionStep?.completed_at && (
                                              <span className="text-xs text-green-600">
                                                Completed {formatRelativeTime(executionStep.completed_at)}
                                              </span>
                                            )}
                                            {stepStatus === 'running' && executionStep?.started_at && (
                                              <span className="text-xs text-blue-600">
                                                Running for {(() => {
                                                  const duration = Date.now() - new Date(executionStep.started_at).getTime();
                                                  if (duration < 60000) return `${Math.floor(duration / 1000)}s`;
                                                  if (duration < 3600000) return `${Math.floor(duration / 60000)}m`;
                                                  return `${Math.floor(duration / 3600000)}h`;
                                                })()}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Error Message */}
                                      {executionStep?.error_message && (
                                        <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                                          <strong>Error:</strong> {executionStep.error_message}
                                        </div>
                                      )}

                                      {/* Result Data */}
                                      {executionStep?.result_data && Object.keys(executionStep.result_data).length > 0 && (
                                        <details className="mt-2">
                                          <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                                            View Result Data
                                          </summary>
                                          <div className="mt-1 p-2 bg-white border border-gray-200 rounded text-xs overflow-auto max-h-32">
                                            <pre>{JSON.stringify(executionStep.result_data, null, 2)}</pre>
                                          </div>
                                        </details>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* REMOVED DUPLICATE Email Action Settings Modal */}
      {false && emailActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Email Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (emailActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== emailActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== emailActionSettingsModal.actionId && 
                        connection.toNodeId !== emailActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Template Selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setEmailActionSettingsModal(prev => ({ ...prev, useTemplate: false, selectedTemplateId: null }))}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      !emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Custom Message
                  </button>
                  <button
                    onClick={() => {
                      console.log('📧 Use Template button clicked!');
                      handleOpenTemplateSelector();
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Use Template
                  </button>
                </div>
                
                {/* Show selected template info */}
                {emailActionSettingsModal.useTemplate && emailActionSettingsModal.selectedTemplateId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="text-sm font-medium text-blue-900">
                      Template Selected: {emailTemplateSelector.templates.find(t => t.id === emailActionSettingsModal.selectedTemplateId)?.name || 'Unknown Template'}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Click "Use Template" again to change template
                    </div>
                  </div>
                )}
              </div>

              {/* Email Subject Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <input
                  ref={emailSubjectInputRef}
                  type="text"
                  value={emailActionSettingsModal.subject}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter the email subject line..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={emailActionSettingsModal.useTemplate}
                />
                
                {/* Subject Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Subject:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailSubjectVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Message
                </label>
                <textarea
                  ref={emailMessageTextareaRef}
                  value={emailActionSettingsModal.message}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={emailActionSettingsModal.useTemplate ? "Template content will be used..." : "Enter the email message to send when this automation triggers..."}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={emailActionSettingsModal.useTemplate}
                />
                
                {/* Message Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Message:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailMessageVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                <p className="text-xs text-gray-500 mt-1">
                  Variables will be automatically replaced with actual data when the email is sent. Supports HTML formatting.
                </p>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send To
                </label>
                <select
                  value={emailActionSettingsModal.recipient}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select who to email...</option>
                  {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom email address input - only show when "custom" is selected */}
                {emailActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="email"
                    value={emailActionSettingsModal.customEmail || ''}
                    onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, customEmail: e.target.value }))}
                    placeholder="Enter custom email address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                  />
                )}
                
                {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').length === 1 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    💡 Connect this action to a trigger (like "Booking Created") to see more email options
                  </p>
                )}
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === emailActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === emailActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Email', 'Booking Confirmation', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmailMessage}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delay Action Settings Modal */}
      {delayActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Wait Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (delayActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== delayActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== delayActionSettingsModal.actionId && 
                        connection.toNodeId !== delayActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Wait Type Selection */}
            <div className="space-y-4">
              {(() => {
                const isCreatedTrigger = delayActionSettingsModal.actionId ? isConnectedToCreatedTrigger(delayActionSettingsModal.actionId) : false;
                
                return (
                  <>
                    {/* Wait Type Dropdown - only show if connected to 'created' trigger */}
                    {isCreatedTrigger && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Type
                        </label>
                        <select
                          value={delayActionSettingsModal.waitType}
                          onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                            ...prev, 
                            waitType: e.target.value as 'duration' | 'reminder'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="duration">Wait Duration</option>
                          <option value="reminder">Reminder</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Choose between a fixed wait duration or a scheduled reminder before appointment.
                        </p>
                      </div>
                    )}

                    {/* Duration Configuration - show for duration type or when not connected to created trigger */}
                    {(delayActionSettingsModal.waitType === 'duration' || !isCreatedTrigger) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Duration
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={delayActionSettingsModal.duration}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              duration: parseInt(e.target.value) || 1 
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <select
                            value={delayActionSettingsModal.unit}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              unit: e.target.value as 'minutes' | 'hours' | 'days' 
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The automation will pause for this duration before continuing to the next action.
                        </p>
                      </div>
                    )}

                    {/* Reminder Configuration - show only for reminder type */}
                    {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notify Time Before
                          </label>
                          <div className="flex gap-3 items-center">
                            <input
                              type="number"
                              min="1"
                              max="168"
                              value={delayActionSettingsModal.reminderHoursBefore}
                              onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                                ...prev, 
                                reminderHoursBefore: parseInt(e.target.value) || 1 
                              }))}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <span className="text-sm text-gray-600">hours before</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Time
                          </label>
                          <select
                            value={delayActionSettingsModal.reminderReference}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderReference: e.target.value as 'drop_off_datetime' | 'start_datetime'
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="drop_off_datetime">Drop Off Date & Time</option>
                            <option value="start_datetime">Start Date & Time</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose which appointment time to calculate the reminder from.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-sm font-medium text-blue-900">Preview:</div>
                      <div className="text-sm text-blue-700">
                        {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger ? (
                          <>
                            Send reminder {delayActionSettingsModal.reminderHoursBefore} hours before{' '}
                            {delayActionSettingsModal.reminderReference === 'drop_off_datetime' ? 'drop off' : 'start'} time
                            <br />
                            <span className="text-xs text-blue-600">
                              Example: For a 2 PM appointment, notification will be sent at{' '}
                              {delayActionSettingsModal.reminderHoursBefore === 24 ? '2 PM the day before' : 
                               delayActionSettingsModal.reminderHoursBefore >= 24 ? 
                               `${Math.floor(delayActionSettingsModal.reminderHoursBefore / 24)} days and ${delayActionSettingsModal.reminderHoursBefore % 24} hours before` :
                               `${2 - delayActionSettingsModal.reminderHoursBefore} ${delayActionSettingsModal.reminderHoursBefore === 1 ? 'PM' : delayActionSettingsModal.reminderHoursBefore === 2 ? 'PM' : 'AM'}`
                              }
                            </span>
                          </>
                        ) : (
                          <>
                            Wait for {delayActionSettingsModal.duration} {delayActionSettingsModal.unit}
                            {(() => {
                              const duration = delayActionSettingsModal.duration;
                              const unit = delayActionSettingsModal.unit;
                              let totalMinutes = 0;
                              
                              if (unit === 'minutes') totalMinutes = duration;
                              else if (unit === 'hours') totalMinutes = duration * 60;
                              else if (unit === 'days') totalMinutes = duration * 24 * 60;
                              
                              if (totalMinutes < 60) {
                                return ` (${totalMinutes} minutes)`;
                              } else if (totalMinutes < 1440) {
                                return ` (${Math.round(totalMinutes / 60 * 10) / 10} hours)`;
                              } else {
                                return ` (${Math.round(totalMinutes / 1440 * 10) / 10} days)`;
                              }
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* UI Label Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UI Label (Optional)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          const currentAction = actions.find(action => action.id === delayActionSettingsModal.actionId);
                          return currentAction?.config?.uiLabel || '';
                        })()}
                        onChange={(e) => {
                          const newUiLabel = e.target.value;
                          setActions(prev => prev.map(action => 
                            action.id === delayActionSettingsModal.actionId
                              ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                              : action
                          ));
                        }}
                        placeholder={
                          delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger 
                            ? "e.g. 'Appointment Reminder', '24hr Reminder', etc."
                            : "e.g. 'Cool Down Period', '24hr Delay', etc."
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add a short label to help identify this action on the canvas
                      </p>
                    </div>

                    {/* Info Box */}
                    {!isCreatedTrigger && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-sm text-yellow-800">
                          <strong>💡 Tip:</strong> Connect this wait action to a "Booking Created" trigger to enable the reminder option for scheduled notifications before appointments!
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!delayActionSettingsModal.actionId || !selectedAutomation?.id) return;

                  try {
                    // Prepare configuration based on wait type
                    const waitConfig: any = {
                      wait_type: delayActionSettingsModal.waitType
                    };

                    if (delayActionSettingsModal.waitType === 'duration') {
                      // Convert to minutes for storage
                      let durationInMinutes = delayActionSettingsModal.duration;
                      if (delayActionSettingsModal.unit === 'hours') {
                        durationInMinutes = delayActionSettingsModal.duration * 60;
                      } else if (delayActionSettingsModal.unit === 'days') {
                        durationInMinutes = delayActionSettingsModal.duration * 24 * 60;
                      }

                      waitConfig.duration_minutes = durationInMinutes;
                      waitConfig.duration_type = delayActionSettingsModal.unit;
                      waitConfig.duration = durationInMinutes; // Legacy field
                      waitConfig.unit = delayActionSettingsModal.unit; // Legacy field
                      waitConfig.displayDuration = delayActionSettingsModal.duration; // Legacy field
                    } else if (delayActionSettingsModal.waitType === 'reminder') {
                      waitConfig.reminder_hours_before = delayActionSettingsModal.reminderHoursBefore;
                      waitConfig.reminder_reference = delayActionSettingsModal.reminderReference;
                    }

                    // Update the action with the new configuration
                    const updatedActions = actions.map(action => 
                      action.id === delayActionSettingsModal.actionId
                        ? { 
                            ...action, 
                            config: { 
                              ...action.config, 
                              ...waitConfig
                            }
                          }
                        : action
                    );

                    // Update local state
                    setActions(updatedActions);

                    // Save to database
                    await saveAutomationActions(selectedAutomation.id, updatedActions);
                    
                    console.log('✅ Wait action configuration saved successfully');

                    // Close modal
                    setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                  } catch (error) {
                    console.error('❌ Error saving wait action configuration:', error);
                    setError('Failed to save wait action configuration');
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create CRM Lead Action Settings Modal */}
      {crmLeadActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create CRM Lead Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (crmLeadActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== crmLeadActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== crmLeadActionSettingsModal.actionId && 
                        connection.toNodeId !== crmLeadActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCrmLeadActionSettingsModal({ isOpen: false, actionId: null });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Settings Content */}
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {/* Pipeline Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pipeline *
                </label>
                <select
                  value={fieldMappings.selectedPipeline || ''}
                  onChange={(e) => {
                    const pipelineId = e.target.value ? parseInt(e.target.value) : null;
                    const selectedPipeline = pipelines.find(p => p.id === pipelineId);
                    console.log('🔧 CRM LEAD: Pipeline selection debug:', {
                      pipelineId,
                      availablePipelines: pipelines.map(p => ({ id: p.id, name: p.pipeline_name })),
                      selectedPipeline,
                      pipelineName: selectedPipeline?.pipeline_name
                    });
                    setFieldMappings(prev => ({
                      ...prev,
                      selectedPipeline: pipelineId,
                      selectedPipelineName: selectedPipeline?.pipeline_name || '',
                      selectedStage: null, // Reset stage when pipeline changes
                      selectedStageName: '' // Reset stage name when pipeline changes
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={loadingPipelines}
                >
                  <option value="">Select a pipeline...</option>
                  {pipelines.map(pipeline => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.pipeline_name}
                    </option>
                  ))}
                </select>
                {loadingPipelines && <p className="text-xs text-gray-500 mt-1">Loading pipelines...</p>}
              </div>

              {/* Stage Selection */}
              {fieldMappings.selectedPipeline && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stage *
                  </label>
                  <select
                    value={fieldMappings.selectedStage || ''}
                    onChange={(e) => {
                      const stageId = e.target.value ? parseInt(e.target.value) : null;
                      const selectedPipeline = pipelines.find(p => p.id === fieldMappings.selectedPipeline);
                      const selectedStage = selectedPipeline?.stages?.find(s => s.id === stageId);
                      setFieldMappings(prev => ({ 
                        ...prev, 
                        selectedStage: stageId,
                        selectedStageName: selectedStage?.name || ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select a stage...</option>
                    {pipelines
                      .find(p => p.id === fieldMappings.selectedPipeline)
                      ?.stages?.map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Field Mappings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700 border-b pb-2">Field Mappings</h4>
                
                {/* Customer Info - Auto-handled */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-green-900 mb-1">Customer Creation</div>
                  <div className="text-xs text-green-700">
                    Customer from lead form
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={fieldMappings.status || 'Open'}
                    onChange={(e) => setFieldMappings(prev => ({
                      ...prev,
                      status: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Open">Open</option>
                    <option value="Won">Won</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>

                {/* Opportunity Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Title *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.title || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        title: e.target.value
                      }))}
                      placeholder="Enter opportunity title"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('title', fieldMappings.title || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Opportunity Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Value
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.value || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        value: e.target.value
                      }))}
                      placeholder="0"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('value', fieldMappings.value || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Business Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.business_name || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        business_name: e.target.value
                      }))}
                      placeholder="Leave blank if not applicable"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('business_name', fieldMappings.business_name || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Opportunity Source */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Source
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.source || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        source: e.target.value
                      }))}
                      placeholder="e.g. Website Form, Lead Magnet"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('source', fieldMappings.source || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.tags || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        tags: e.target.value
                      }))}
                      placeholder="e.g. Hot Lead, Web Form"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('tags', fieldMappings.tags || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Custom Fields */}
                {customFieldDefinitions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Fields
                    </label>
                    <div className="space-y-3">
                      {customFieldDefinitions.map(fieldDef => (
                        <div key={fieldDef.id}>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {fieldDef.name}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={fieldMappings.custom_fields[fieldDef.id] || ''}
                              onChange={(e) => setFieldMappings(prev => ({
                                ...prev,
                                custom_fields: {
                                  ...prev.custom_fields,
                                  [fieldDef.id]: e.target.value
                                }
                              }))}
                              placeholder={`Enter ${fieldDef.name.toLowerCase()}`}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <button
                              type="button"
                              onClick={() => handleOpenVariablesModal(`custom_fields.${fieldDef.id}`, fieldMappings.custom_fields[fieldDef.id] || '')}
                              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              title="Insert form field variable"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === crmLeadActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === crmLeadActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Create Lead', 'Add to Pipeline', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!crmLeadActionSettingsModal.actionId) return;

                  // Validate required fields
                  if (!fieldMappings.selectedPipeline || !fieldMappings.selectedStage) {
                    alert('Please select a pipeline and stage');
                    return;
                  }

                  if (!fieldMappings.title) {
                    alert('Please provide an opportunity title');
                    return;
                  }

                  // Update the action with the field mappings configuration
                  setActions(prev => prev.map(action => 
                    action.id === crmLeadActionSettingsModal.actionId
                      ? { 
                          ...action, 
                          config: { 
                            ...action.config,
                            selectedPipeline: fieldMappings.selectedPipeline,
                            selectedPipelineName: fieldMappings.selectedPipelineName,
                            selectedStage: fieldMappings.selectedStage,
                            selectedStageName: fieldMappings.selectedStageName,
                            title: fieldMappings.title,
                            value: fieldMappings.value,
                            business_name: fieldMappings.business_name,
                            source: fieldMappings.source,
                            tags: fieldMappings.tags,
                            status: fieldMappings.status || 'Open',
                            custom_fields: fieldMappings.custom_fields,
                            formFields: selectedFormFields // Store available form fields for reference
                          }
                        }
                      : action
                  ));

                  // Close modal
                  setCrmLeadActionSettingsModal({ isOpen: false, actionId: null });
                }}
                disabled={!fieldMappings.selectedPipeline || !fieldMappings.selectedStage || !fieldMappings.title}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check Action Settings Modal */}
      {checkActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Check Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (checkActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== checkActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== checkActionSettingsModal.actionId && 
                        connection.toNodeId !== checkActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCheckActionSettingsModal({ 
                        isOpen: false, 
                        actionId: null, 
                        checkType: 'feedback',
                        ifCompleted: 'stop',
                        ifNotCompleted: 'continue'
                      });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCheckActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    checkType: 'feedback',
                    ifCompleted: 'stop',
                    ifNotCompleted: 'continue'
                  })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Settings Content */}
            <div className="space-y-6">
                             {/* Check Type Selection */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Check Type
                 </label>
                 <select
                   value={checkActionSettingsModal.checkType}
                   onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                     ...prev, 
                     checkType: e.target.value as 'feedback' 
                   }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                 >
                   {(() => {
                     // Check if this action is connected to a workflow with a booking trigger
                     const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                       isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
                     
                     if (isConnectedToBookingTrigger) {
                       return <option value="feedback">Feedback Status</option>;
                     } else {
                       return <option value="">No check types available</option>;
                     }
                   })()}
                 </select>
                 {(() => {
                   const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                     isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
                   
                   if (!isConnectedToBookingTrigger) {
                     return (
                       <p className="text-xs text-gray-500 mt-1">
                         Connect this action to a workflow with a booking trigger to enable feedback checking.
                       </p>
                     );
                   }
                   return null;
                                })()}
             </div>

             {/* If Settings - only show when connected to booking trigger and feedback status is selected */}
             {(() => {
               const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                 isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
               
               if (isConnectedToBookingTrigger && checkActionSettingsModal.checkType === 'feedback') {
                 return (
                   <div className="space-y-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         If Feedback Completed
                       </label>
                       <select
                         value={checkActionSettingsModal.ifCompleted}
                         onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                           ...prev, 
                           ifCompleted: e.target.value as 'stop' | 'continue'
                         }))}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                       >
                         <option value="stop">Stop Automation</option>
                         <option value="continue">Continue to Next Action</option>
                       </select>
                     </div>
                     
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         If Feedback Not Completed
                       </label>
                       <select
                         value={checkActionSettingsModal.ifNotCompleted}
                         onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                           ...prev, 
                           ifNotCompleted: e.target.value as 'stop' | 'continue'
                         }))}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                       >
                         <option value="stop">Stop Automation</option>
                         <option value="continue">Continue to Next Action</option>
                       </select>
                     </div>
                   </div>
                 );
               }
               return null;
             })()}

             {/* Feedback Info - only show when connected to booking trigger */}
               {(() => {
                 const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                   isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
                 
                 if (isConnectedToBookingTrigger) {
                   return null;
                 } else {
                   return (
                     <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                       <div className="flex items-start">
                         <div className="flex-shrink-0">
                           <Eye className="h-5 w-5 text-gray-600" />
                         </div>
                         <div className="ml-3">
                           <h4 className="text-sm font-medium text-gray-800">No Check Types Available</h4>
                           <p className="text-sm text-gray-700 mt-1">
                             This action needs to be connected to a workflow with a booking trigger to enable feedback checking.
                           </p>
                         </div>
                       </div>
                     </div>
                   );
                 }
               })()}


            </div>

            {/* Footer */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCheckActionSettingsModal({ 
                  isOpen: false, 
                  actionId: null, 
                  checkType: 'feedback'
                })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save the check action configuration
                  if (checkActionSettingsModal.actionId) {
                    const isConnectedToBookingTrigger = isConnectedToCreatedTrigger(checkActionSettingsModal.actionId);
                    
                    if (isConnectedToBookingTrigger && checkActionSettingsModal.checkType === 'feedback') {
                      setActions(prev => prev.map(action => {
                        if (action.id === checkActionSettingsModal.actionId) {
                          return {
                            ...action,
                            config: {
                              checkType: checkActionSettingsModal.checkType,
                              ifCompleted: checkActionSettingsModal.ifCompleted,
                              ifNotCompleted: checkActionSettingsModal.ifNotCompleted,
                              uiLabel: 'Check Booking Feedback'
                            }
                          };
                        }
                        return action;
                      }));
                    }
                  }
                  
                  // Close modal
                  setCheckActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    checkType: 'feedback',
                    ifCompleted: 'stop',
                    ifNotCompleted: 'continue'
                  });
                }}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Action Settings Modal */}
      {createTaskActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create Task Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (createTaskActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== createTaskActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== createTaskActionSettingsModal.actionId && 
                        connection.toNodeId !== createTaskActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCreateTaskActionSettingsModal({ 
                        isOpen: false, 
                        actionId: null, 
                        selectedBoardId: '',
                        selectedColumnId: '',
                        taskTitle: '',
                        taskDescription: '',
                        taskUrgency: 'medium',
                        taskStatus: 'pending',
                        scheduleOffset: 1,
                        scheduleUnit: 'days',
                        boards: [],
                        columns: []
                      });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCreateTaskActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    selectedBoardId: '',
                    selectedColumnId: '',
                    taskTitle: '',
                    taskDescription: '',
                    taskUrgency: 'medium',
                    taskStatus: 'pending',
                    scheduleOffset: 1,
                    scheduleUnit: 'days',
                    boards: [],
                    columns: []
                  })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Settings Content */}
            <div className="space-y-6">
              {/* Board Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Board
                </label>
                <select
                  value={createTaskActionSettingsModal.selectedBoardId}
                  onChange={(e) => {
                    const boardId = e.target.value;
                    console.log('🎯 Board selected:', boardId);
                    setCreateTaskActionSettingsModal(prev => ({ 
                      ...prev, 
                      selectedBoardId: boardId,
                      selectedColumnId: '' // Reset column when board changes
                    }));
                    
                    // Load columns for the selected board
                    if (boardId && selectedBusiness?.id) {
                      console.log('🎯 Loading columns for board:', boardId);
                      getBoardWithColumns(boardId).then(boardWithColumns => {
                        console.log('🎯 Board with columns:', boardWithColumns);
                        console.log('🎯 Columns found:', boardWithColumns.columns.length);
                        setCreateTaskActionSettingsModal(prev => ({
                          ...prev,
                          columns: boardWithColumns.columns.map(column => ({
                            id: column.id,
                            title: column.title
                          }))
                        }));
                      }).catch(error => {
                        console.error('❌ Error loading columns:', error);
                      });
                    } else {
                      console.log('🎯 No board selected or no business ID');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Choose a board...</option>
                  {createTaskActionSettingsModal.boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.board_title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select List
                </label>
                {console.log('🎯 Rendering column dropdown with columns:', createTaskActionSettingsModal.columns.length)}
                <select
                  value={createTaskActionSettingsModal.selectedColumnId}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    selectedColumnId: e.target.value 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={!createTaskActionSettingsModal.selectedBoardId}
                >
                  <option value="">Choose a list...</option>
                  {createTaskActionSettingsModal.columns.map((column) => (
                    <option key={column.id} value={column.id}>
                      {column.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Available columns: {createTaskActionSettingsModal.columns.length}
                </p>
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title
                </label>
                <input
                  type="text"
                  value={createTaskActionSettingsModal.taskTitle}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskTitle: e.target.value 
                  }))}
                  placeholder="Enter task title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                
                {/* Variable Dropdown for Task Title */}
                {(() => {
                  const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                  console.log('🎯 Available variables for task title:', availableVariables.length);
                  console.log('🎯 Current columns state:', createTaskActionSettingsModal.columns.length);
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              // Insert variable into task title
                              const currentValue = createTaskActionSettingsModal.taskTitle;
                              const newValue = currentValue + e.target.value;
                              setCreateTaskActionSettingsModal(prev => ({ 
                                ...prev, 
                                taskTitle: newValue 
                              }));
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Task Description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Task Description
                  </label>
                  {(() => {
                    const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                    return availableVariables.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setTaskComponentsModal({
                          isOpen: true,
                          currentDescription: createTaskActionSettingsModal.taskDescription
                        })}
                        className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50"
                        title="Add Components"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    ) : null;
                  })()}
                </div>
                <textarea
                  value={createTaskActionSettingsModal.taskDescription}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskDescription: e.target.value 
                  }))}
                  placeholder="Enter task description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
                
                {/* Variable Dropdown for Task Description */}
                {(() => {
                  const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              // Insert variable into task description
                              const currentValue = createTaskActionSettingsModal.taskDescription;
                              const newValue = currentValue + e.target.value;
                              setCreateTaskActionSettingsModal(prev => ({ 
                                ...prev, 
                                taskDescription: newValue 
                              }));
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Task Urgency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Urgency Level
                </label>
                <select
                  value={createTaskActionSettingsModal.taskUrgency}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskUrgency: e.target.value as 'low' | 'medium' | 'high' | 'alert'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="alert">Alert</option>
                </select>
              </div>

              {/* Task Status and Scheduling */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Status
                </label>
                <select
                  value={createTaskActionSettingsModal.taskStatus}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskStatus: e.target.value as 'pending' | 'scheduled'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>

              {/* Scheduling Options - only show when status is scheduled */}
              {createTaskActionSettingsModal.taskStatus === 'scheduled' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Task
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={createTaskActionSettingsModal.scheduleOffset}
                        onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                          ...prev, 
                          scheduleOffset: parseInt(e.target.value) || 1
                        }))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <select
                        value={createTaskActionSettingsModal.scheduleUnit}
                        onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                          ...prev, 
                          scheduleUnit: e.target.value as 'hours' | 'days' | 'weeks'
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="hours">Hour(s)</option>
                        <option value="days">Day(s)</option>
                        <option value="weeks">Week(s)</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Task will be scheduled {createTaskActionSettingsModal.scheduleOffset} {createTaskActionSettingsModal.scheduleUnit} from when the automation runs
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCreateTaskActionSettingsModal({ 
                  isOpen: false, 
                  actionId: null, 
                  selectedBoardId: '',
                  selectedColumnId: '',
                  taskTitle: '',
                  taskDescription: '',
                  taskUrgency: 'medium',
                  taskStatus: 'pending',
                  scheduleOffset: 1,
                  scheduleUnit: 'days',
                  boards: [],
                  columns: []
                })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save the create task action configuration
                  if (createTaskActionSettingsModal.actionId) {
                    setActions(prev => prev.map(action => {
                      if (action.id === createTaskActionSettingsModal.actionId) {
                        return {
                          ...action,
                          config: {
                            selectedBoardId: createTaskActionSettingsModal.selectedBoardId,
                            selectedColumnId: createTaskActionSettingsModal.selectedColumnId,
                            taskTitle: createTaskActionSettingsModal.taskTitle,
                            taskDescription: createTaskActionSettingsModal.taskDescription,
                            taskUrgency: createTaskActionSettingsModal.taskUrgency,
                            taskStatus: createTaskActionSettingsModal.taskStatus,
                            scheduleOffset: createTaskActionSettingsModal.scheduleOffset,
                            scheduleUnit: createTaskActionSettingsModal.scheduleUnit,
                            uiLabel: `Create Task: ${createTaskActionSettingsModal.taskTitle || 'Untitled'}`
                          }
                        };
                      }
                      return action;
                    }));
                  }
                  
                  // Close modal
                  setCreateTaskActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    selectedBoardId: '',
                    selectedColumnId: '',
                    taskTitle: '',
                    taskDescription: '',
                    taskUrgency: 'medium',
                    taskStatus: 'pending',
                    scheduleOffset: 1,
                    scheduleUnit: 'days',
                    boards: [],
                    columns: []
                  });
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Title Modal */}
      {isEditTitleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit Automation Title</h2>
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Automation Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter automation title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editTitle.trim()) {
                    handleSaveTitle();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={!editTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Automation Modal */}
      {isDeleteAutomationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Delete Automation</h2>
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Are you sure?</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                This will permanently delete the automation "{selectedAutomation?.name}" and all of its:
              </p>
              <ul className="text-sm text-gray-600 mt-2 ml-4 list-disc">
                <li>Triggers and actions</li>
                <li>Connections and workflow</li>
                <li>Execution history</li>
                <li>Scheduled jobs</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Automation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Components Modal */}
      {taskComponentsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Task Components</h3>
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Click on any component below to add it to your task description:
              </p>
              
              <div className="space-y-3">
                {/* Customer Follow Component */}
                <button
                  onClick={() => {
                    const customerFollowComponent = `
[customer_follow]
Customer: {customer_name}
Booking: {booking_number}
Vehicle: {vehicle_info}
Service: {booking_title}
Total: {grand_total}
[/customer_follow]`;
                    
                    const newDescription = taskComponentsModal.currentDescription + customerFollowComponent;
                    setCreateTaskActionSettingsModal(prev => ({
                      ...prev,
                      taskDescription: newDescription
                    }));
                    setTaskComponentsModal({ isOpen: false, currentDescription: '' });
                  }}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <User className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Customer Follow</span>
                        <p className="text-xs text-gray-600">Add customer and booking information</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variables Modal */}
      {variablesModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Insert Form Field Variable</h3>
              <button
                onClick={handleCloseVariablesModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Click on any form field below to insert it as a variable:
              </p>
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {selectedFormFields.length > 0 ? (
                  selectedFormFields.map(field => {
                    // Create simple variable names based on field labels
                    let displayVariable = `{{${field.id}}}`;
                    
                    // Map to simple variable names based on label
                    if (field.label === 'First Name') {
                      displayVariable = '{{first_name}}';
                    } else if (field.label === 'Last Name') {
                      displayVariable = '{{last_name}}';
                    } else if (field.label === 'Email Address') {
                      displayVariable = '{{email}}';
                    } else if (field.label === 'Phone Number') {
                      displayVariable = '{{phone}}';
                    } else {
                      // For other fields, use a cleaned version of the label
                      const cleanLabel = field.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                      displayVariable = `{{${cleanLabel}}}`;
                    }
                    
                    return (
                      <button
                        key={field.id}
                        onClick={() => insertVariableIntoField(displayVariable)}
                        className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{field.label}</span>
                          <span className="text-xs text-gray-500">{field.type}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Variable: <code className="bg-gray-100 px-1 rounded">{displayVariable}</code>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No form fields available.</p>
                    <p className="text-sm mt-1">Make sure you have a Form Submission trigger connected to this action.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCloseVariablesModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Selector Modal */}
      {(() => {
        console.log('📧 RENDER CHECK: emailTemplateSelector.isOpen =', emailTemplateSelector.isOpen);
        return emailTemplateSelector.isOpen;
      })() && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Select Email Template</h3>
              <button
                onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto">
              {emailTemplateSelector.loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading templates...</div>
                </div>
              ) : emailTemplateSelector.templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No Email Templates Found</p>
                  <p className="text-sm">Create email templates in the Marketing page first.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailTemplateSelector.templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          {template.subject && (
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>Subject:</strong> {template.subject}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              template.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : template.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {template.status}
                            </span>
                            {template.category && (
                              <span className="text-xs text-gray-500">
                                {template.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {new Date(template.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  // Main component return with modals available for both desktop and mobile
  return (
    <>
      {renderMainContent()}
      
      {/* Create Automation Modal - Available for both desktop and mobile */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="bg-gray-900 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
              <h2 className="text-lg font-semibold">Create New Automation</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Automation Name
                </label>
                <input
                  type="text"
                  value={automationTitle}
                  onChange={(e) => setAutomationTitle(e.target.value)}
                  placeholder="Enter automation name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && automationTitle.trim()) {
                      handleSaveAutomation();
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 rounded-b-lg flex gap-3">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAutomation}
                disabled={!automationTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Automation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trigger Library Modal - Available for both desktop and mobile */}
      {isTriggerLibraryOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Add Trigger</h2>
              <button
                onClick={() => setIsTriggerLibraryOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid gap-4">
              {availableTriggers.map((trigger) => (
                <div
                  key={trigger.id}
                  onClick={() => handleSelectTrigger(trigger)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${trigger.color}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{trigger.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">{trigger.name}</h3>
                      <p className="text-sm text-gray-600">{trigger.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action Library Modal - Available for both desktop and mobile */}
      {isActionLibraryOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Add Action</h2>
              <button
                onClick={() => setIsActionLibraryOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid gap-4">
              {availableActions.map((action) => (
                <div
                  key={action.id}
                  onClick={() => handleSelectAction(action)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${action.color}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{action.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">{action.name}</h3>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SMS Action Settings Modal - Available for both desktop and mobile */}
      {smsActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">SMS Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (smsActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== smsActionSettingsModal.actionId));
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== smsActionSettingsModal.actionId && connection.toNodeId !== smsActionSettingsModal.actionId
                      ));
                    }
                    setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* SMS Message Input */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMS Message
                </label>
                <textarea
                  ref={messageTextareaRef}
                  value={smsActionSettingsModal.message}
                  onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter the SMS message to send when this automation triggers..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
                
                {/* Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(smsActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                <p className="text-xs text-gray-500 mt-1">
                  Variables will be automatically replaced with actual booking data when the SMS is sent.
                </p>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notify
                </label>
                <select
                  value={smsActionSettingsModal.recipient}
                  onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select who to notify...</option>
                  {getAvailableRecipients(smsActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom phone number input - only show when "custom" is selected */}
                {smsActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="tel"
                    value={smsActionSettingsModal.customPhone || ''}
                    onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, customPhone: e.target.value }))}
                    placeholder="Enter custom phone number..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent mt-2"
                  />
                )}
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const action = actions.find(a => a.id === smsActionSettingsModal.actionId);
                    return action?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === smsActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Message', 'Reminder SMS', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSmsMessage}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Action Settings Modal - Available for both desktop and mobile */}
      {emailActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Email Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (emailActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== emailActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== emailActionSettingsModal.actionId && 
                        connection.toNodeId !== emailActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Template Selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setEmailActionSettingsModal(prev => ({ ...prev, useTemplate: false, selectedTemplateId: null }))}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      !emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Custom Message
                  </button>
                  <button
                    onClick={() => {
                      console.log('📧 Use Template button clicked!');
                      handleOpenTemplateSelector();
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Use Template
                  </button>
                </div>
                
                {/* Show selected template info */}
                {emailActionSettingsModal.useTemplate && emailActionSettingsModal.selectedTemplateId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="text-sm font-medium text-blue-900">
                      Template Selected: {emailTemplateSelector.templates.find(t => t.id === emailActionSettingsModal.selectedTemplateId)?.name || 'Unknown Template'}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Click "Use Template" again to change template
                    </div>
                  </div>
                )}
              </div>

              {/* Email Subject Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <input
                  ref={emailSubjectInputRef}
                  type="text"
                  value={emailActionSettingsModal.subject}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter the email subject line..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={emailActionSettingsModal.useTemplate}
                />
                
                {/* Variable Dropdown for Subject */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0 && !emailActionSettingsModal.useTemplate) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Subject:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailSubjectVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else if (!emailActionSettingsModal.useTemplate) {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Email Message Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Message
                </label>
                <textarea
                  ref={emailMessageTextareaRef}
                  value={emailActionSettingsModal.message}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter the email message content..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={emailActionSettingsModal.useTemplate}
                />
                
                {/* Variable Dropdown for Message */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0 && !emailActionSettingsModal.useTemplate) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Message:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailMessageVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else if (!emailActionSettingsModal.useTemplate) {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                {!emailActionSettingsModal.useTemplate && (
                  <p className="text-xs text-gray-500 mt-1">
                    Variables will be automatically replaced with actual booking data when the email is sent.
                  </p>
                )}
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send To
                </label>
                <select
                  value={emailActionSettingsModal.recipient}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select who to email...</option>
                  {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom email input - only show when "custom" is selected */}
                {emailActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="email"
                    value={emailActionSettingsModal.customEmail || ''}
                    onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, customEmail: e.target.value }))}
                    placeholder="Enter custom email address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                  />
                )}
                
                {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').length === 1 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    💡 Connect this action to a trigger (like "Booking Created") to see more email options
                  </p>
                )}
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === emailActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === emailActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Email', 'Booking Confirmation', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmailMessage}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delay Action Settings Modal - Available for both desktop and mobile */}
      {delayActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Wait Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (delayActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== delayActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== delayActionSettingsModal.actionId && 
                        connection.toNodeId !== delayActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Wait Type Selection */}
            <div className="space-y-4">
              {(() => {
                const isCreatedTrigger = delayActionSettingsModal.actionId ? isConnectedToCreatedTrigger(delayActionSettingsModal.actionId) : false;
                
                return (
                  <>
                    {/* Wait Type Dropdown - only show if connected to 'created' trigger */}
                    {isCreatedTrigger && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Type
                        </label>
                        <select
                          value={delayActionSettingsModal.waitType}
                          onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                            ...prev, 
                            waitType: e.target.value as 'duration' | 'reminder'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="duration">Wait Duration</option>
                          <option value="reminder">Reminder</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Choose between a fixed wait duration or a scheduled reminder before appointment.
                        </p>
                      </div>
                    )}

                    {/* Duration Configuration - show for duration type or when not connected to created trigger */}
                    {(delayActionSettingsModal.waitType === 'duration' || !isCreatedTrigger) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Duration
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={delayActionSettingsModal.duration}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              duration: parseInt(e.target.value) || 1 
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <select
                            value={delayActionSettingsModal.unit}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              unit: e.target.value as 'minutes' | 'hours' | 'days' 
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The automation will pause for this duration before continuing to the next action.
                        </p>
                      </div>
                    )}

                    {/* Reminder Configuration - only show for reminder type and created trigger */}
                    {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hours Before Appointment
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="168"
                            value={delayActionSettingsModal.reminderHoursBefore}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderHoursBefore: parseInt(e.target.value) || 24 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            How many hours before the appointment should the reminder be sent? (1-168 hours)
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Time
                          </label>
                          <select
                            value={delayActionSettingsModal.reminderReference}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderReference: e.target.value as 'drop_off_datetime' | 'start_datetime'
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="drop_off_datetime">Drop Off Date & Time</option>
                            <option value="start_datetime">Start Date & Time</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose which appointment time to calculate the reminder from.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-sm font-medium text-blue-900">Preview:</div>
                      <div className="text-sm text-blue-700">
                        {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger ? (
                          <>
                            Send reminder {delayActionSettingsModal.reminderHoursBefore} hours before{' '}
                            {delayActionSettingsModal.reminderReference === 'drop_off_datetime' ? 'drop off' : 'start'} time
                            <br />
                            <span className="text-xs text-blue-600">
                              Example: For a 2 PM appointment, notification will be sent at{' '}
                              {delayActionSettingsModal.reminderHoursBefore === 24 ? '2 PM the day before' : 
                               delayActionSettingsModal.reminderHoursBefore >= 24 ? 
                               `${Math.floor(delayActionSettingsModal.reminderHoursBefore / 24)} days and ${delayActionSettingsModal.reminderHoursBefore % 24} hours before` :
                               `${2 - delayActionSettingsModal.reminderHoursBefore} ${delayActionSettingsModal.reminderHoursBefore === 1 ? 'PM' : delayActionSettingsModal.reminderHoursBefore === 2 ? 'PM' : 'AM'}`
                              }
                            </span>
                          </>
                        ) : (
                          <>
                            Wait for {delayActionSettingsModal.duration} {delayActionSettingsModal.unit}
                            {(() => {
                              const duration = delayActionSettingsModal.duration;
                              const unit = delayActionSettingsModal.unit;
                              let totalMinutes = 0;
                              
                              if (unit === 'minutes') totalMinutes = duration;
                              else if (unit === 'hours') totalMinutes = duration * 60;
                              else if (unit === 'days') totalMinutes = duration * 24 * 60;
                              
                              if (totalMinutes < 60) {
                                return ` (${totalMinutes} minutes)`;
                              } else if (totalMinutes < 1440) {
                                return ` (${Math.round(totalMinutes / 60 * 10) / 10} hours)`;
                              } else {
                                return ` (${Math.round(totalMinutes / 1440 * 10) / 10} days)`;
                              }
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* UI Label Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UI Label (Optional)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          const currentAction = actions.find(action => action.id === delayActionSettingsModal.actionId);
                          return currentAction?.config?.uiLabel || '';
                        })()}
                        onChange={(e) => {
                          const newUiLabel = e.target.value;
                          setActions(prev => prev.map(action => 
                            action.id === delayActionSettingsModal.actionId
                              ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                              : action
                          ));
                        }}
                        placeholder={
                          delayActionSettingsModal.waitType === 'reminder' && delayActionSettingsModal.actionId && isConnectedToCreatedTrigger(delayActionSettingsModal.actionId)
                            ? "e.g. 'Appointment Reminder', '24hr Reminder', etc."
                            : "e.g. 'Cool Down Period', '24hr Delay', etc."
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add a short label to help identify this action on the canvas
                      </p>
                    </div>

                    {/* Info Box */}
                    {!isCreatedTrigger && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-sm text-yellow-800">
                          <strong>💡 Tip:</strong> Connect this wait action to a "Booking Created" trigger to enable the reminder option for scheduled notifications before appointments!
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!delayActionSettingsModal.actionId || !selectedAutomation?.id) return;

                  try {
                    // Prepare configuration based on wait type
                    const waitConfig: any = {
                      wait_type: delayActionSettingsModal.waitType
                    };

                    if (delayActionSettingsModal.waitType === 'duration') {
                      // Convert to minutes for storage
                      let durationInMinutes = delayActionSettingsModal.duration;
                      if (delayActionSettingsModal.unit === 'hours') {
                        durationInMinutes = delayActionSettingsModal.duration * 60;
                      } else if (delayActionSettingsModal.unit === 'days') {
                        durationInMinutes = delayActionSettingsModal.duration * 24 * 60;
                      }

                      waitConfig.duration_minutes = durationInMinutes;
                      waitConfig.duration_type = delayActionSettingsModal.unit;
                      waitConfig.duration = durationInMinutes; // Legacy field
                      waitConfig.unit = delayActionSettingsModal.unit; // Legacy field
                      waitConfig.displayDuration = delayActionSettingsModal.duration; // Legacy field
                    } else if (delayActionSettingsModal.waitType === 'reminder') {
                      waitConfig.reminder_hours_before = delayActionSettingsModal.reminderHoursBefore;
                      waitConfig.reminder_reference = delayActionSettingsModal.reminderReference;
                    }

                    // Update the action with the configuration
                    const updatedActions = actions.map(action => 
                      action.id === delayActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, ...waitConfig } }
                        : action
                    );

                    // Update local state
                    setActions(updatedActions);

                    // Save to database
                    await saveAutomationActions(selectedAutomation.id, updatedActions);
                    
                    console.log('✅ Wait action configuration saved successfully (mobile)');

                    // Close modal
                    setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                  } catch (error) {
                    console.error('❌ Error saving wait action configuration (mobile):', error);
                    setError('Failed to save wait action configuration');
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CRM Lead Action Settings Modal - Available for both desktop and mobile */}
      {crmLeadActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create CRM Lead Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (crmLeadActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== crmLeadActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== crmLeadActionSettingsModal.actionId && 
                        connection.toNodeId !== crmLeadActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCrmLeadActionSettingsModal({ isOpen: false, actionId: null });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-900 mb-2">Create CRM Lead</div>
                <div className="text-sm text-blue-700">
                  This action will automatically create a new lead in your CRM system when triggered.
                  The lead will include all available booking information such as customer details,
                  service requested, and appointment times.
                </div>
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === crmLeadActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === crmLeadActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'New Lead Creation', 'CRM Integration', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Close modal - no additional configuration needed for CRM lead creation
                  setCrmLeadActionSettingsModal({ isOpen: false, actionId: null });
                }}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check Action Settings Modal - Available for both desktop and mobile */}
      {checkActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Check Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (checkActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== checkActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== checkActionSettingsModal.actionId && 
                        connection.toNodeId !== checkActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Check Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check Type
                </label>
                <select
                  value={checkActionSettingsModal.checkType}
                  onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                    ...prev, 
                    checkType: e.target.value as 'feedback'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="feedback">Customer Feedback</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose what type of check to perform
                </p>
              </div>

              {/* If Completed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  If Check Completed
                </label>
                <select
                  value={checkActionSettingsModal.ifCompleted}
                  onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                    ...prev, 
                    ifCompleted: e.target.value as 'stop' | 'continue'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="stop">Stop Automation</option>
                  <option value="continue">Continue to Next Action</option>
                </select>
              </div>

              {/* If Not Completed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  If Check Not Completed
                </label>
                <select
                  value={checkActionSettingsModal.ifNotCompleted}
                  onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                    ...prev, 
                    ifNotCompleted: e.target.value as 'stop' | 'continue'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="stop">Stop Automation</option>
                  <option value="continue">Continue to Next Action</option>
                </select>
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === checkActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === checkActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Feedback Check', 'Quality Control', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!checkActionSettingsModal.actionId) return;

                  // Update the action with the configuration
                  setActions(prev => prev.map(action => 
                    action.id === checkActionSettingsModal.actionId
                      ? { 
                          ...action, 
                          config: { 
                            ...action.config,
                            checkType: checkActionSettingsModal.checkType,
                            ifCompleted: checkActionSettingsModal.ifCompleted,
                            ifNotCompleted: checkActionSettingsModal.ifNotCompleted
                          } 
                        }
                      : action
                  ));

                  // Close modal
                  setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' });
                }}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Action Settings Modal - Available for both desktop and mobile */}
      {createTaskActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create Task Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (createTaskActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== createTaskActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== createTaskActionSettingsModal.actionId && 
                        connection.toNodeId !== createTaskActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCreateTaskActionSettingsModal({ isOpen: false, actionId: null, selectedBoardId: '', selectedColumnId: '', taskTitle: '', taskDescription: '', assignedTo: '', dueDate: '', priority: 'medium' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCreateTaskActionSettingsModal({ isOpen: false, actionId: null, selectedBoardId: '', selectedColumnId: '', taskTitle: '', taskDescription: '', assignedTo: '', dueDate: '', priority: 'medium' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Board Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Board
                </label>
                <select
                  value={createTaskActionSettingsModal.selectedBoardId}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    selectedBoardId: e.target.value,
                    selectedColumnId: '' // Reset column when board changes
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select a board...</option>
                  {/* Board options would be populated here */}
                </select>
              </div>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Column
                </label>
                <select
                  value={createTaskActionSettingsModal.selectedColumnId}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    selectedColumnId: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={!createTaskActionSettingsModal.selectedBoardId}
                >
                  <option value="">Select a column...</option>
                  {/* Column options would be populated based on selected board */}
                </select>
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title
                </label>
                <input
                  type="text"
                  value={createTaskActionSettingsModal.taskTitle}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskTitle: e.target.value
                  }))}
                  placeholder="Enter task title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === createTaskActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === createTaskActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Follow-up Task', 'Service Reminder', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setCreateTaskActionSettingsModal({ isOpen: false, actionId: null, selectedBoardId: '', selectedColumnId: '', taskTitle: '', taskDescription: '', assignedTo: '', dueDate: '', priority: 'medium' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!createTaskActionSettingsModal.actionId) return;

                  // Update the action with the configuration
                  setActions(prev => prev.map(action => 
                    action.id === createTaskActionSettingsModal.actionId
                      ? { 
                          ...action, 
                          config: { 
                            ...action.config,
                            selectedBoardId: createTaskActionSettingsModal.selectedBoardId,
                            selectedColumnId: createTaskActionSettingsModal.selectedColumnId,
                            taskTitle: createTaskActionSettingsModal.taskTitle
                          } 
                        }
                      : action
                  ));

                  // Close modal
                  setCreateTaskActionSettingsModal({ isOpen: false, actionId: null, selectedBoardId: '', selectedColumnId: '', taskTitle: '', taskDescription: '', assignedTo: '', dueDate: '', priority: 'medium' });
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Components Modal - Available for both desktop and mobile */}
      {taskComponentsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Task Components</h3>
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Click on any component below to add it to your task description:
              </p>
              
              <div className="space-y-3">
                {/* Customer Follow Component */}
                <button
                  onClick={() => {
                    const customerFollowComponent = `
[customer_follow]
Customer: {customer_name}
Booking: {booking_number}
Vehicle: {vehicle_info}
Service: {booking_title}
Total: {grand_total}
[/customer_follow]`;
                    
                    const newDescription = taskComponentsModal.currentDescription + customerFollowComponent;
                    setCreateTaskActionSettingsModal(prev => ({
                      ...prev,
                      taskDescription: newDescription
                    }));
                    setTaskComponentsModal({ isOpen: false, currentDescription: '' });
                  }}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <User className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Customer Follow</span>
                        <p className="text-xs text-gray-600">Add customer and booking information</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variables Modal - Available for both desktop and mobile */}
      {variablesModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Insert Form Field Variable</h3>
              <button
                onClick={handleCloseVariablesModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a form field to insert as a variable:
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedFormFields && selectedFormFields.length > 0 ? (
                  selectedFormFields.map((field) => {
                    // Create display variable based on field type
                    let displayVariable = `{{${field.label}}}`;
                    
                    // For specific field types, use cleaner variable names
                    if (field.type === 'email') {
                      displayVariable = '{{email}}';
                    } else if (field.type === 'phone') {
                      displayVariable = '{{phone}}';
                    } else if (field.label.toLowerCase().includes('name')) {
                      const cleanLabel = field.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                      displayVariable = `{{${cleanLabel}}}`;
                    }
                    
                    return (
                      <button
                        key={field.id}
                        onClick={() => insertVariableIntoField(displayVariable)}
                        className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{field.label}</span>
                          <span className="text-xs text-gray-500">{field.type}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Variable: <code className="bg-gray-100 px-1 rounded">{displayVariable}</code>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No form fields available.</p>
                    <p className="text-sm mt-1">Make sure you have a Form Submission trigger connected to this action.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCloseVariablesModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Selector Modal - Available for both desktop and mobile */}
      {emailTemplateSelector.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Select Email Template</h3>
              <button
                onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto">
              {emailTemplateSelector.loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading templates...</div>
                </div>
              ) : emailTemplateSelector.templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No Email Templates Found</p>
                  <p className="text-sm">Create email templates in the Marketing page first.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailTemplateSelector.templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          {template.subject && (
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>Subject:</strong> {template.subject}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              template.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : template.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {template.status}
                            </span>
                            {template.category && (
                              <span className="text-xs text-gray-500">
                                {template.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {new Date(template.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Title Modal - Available for both desktop and mobile */}
      {isEditTitleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit Automation Title</h2>
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Automation Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter automation title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editTitle.trim()) {
                    handleSaveTitle();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={!editTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Automation Modal - Available for both desktop and mobile */}
      {isDeleteAutomationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Delete Automation</h2>
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete this automation? This action cannot be undone.
              </p>
              {selectedAutomation && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{selectedAutomation.title}</p>
                  <p className="text-sm text-gray-600">
                    Status: {selectedAutomation.is_live ? 'Live' : 'Draft'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAutomation}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Execution Details Modal - Available for both desktop and mobile */}
      {isExecutionDetailsOpen && selectedExecutionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 h-5/6">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Execution Details</h2>
              <button
                onClick={handleCloseExecutionDetails}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 h-full overflow-y-auto">
              {loadingSteps ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading execution details...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Execution Overview */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Execution Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            const actualStatus = execution ? getActualExecutionStatus(execution) : 'pending';
                            return (
                              <>
                                {getStatusIcon(actualStatus)}
                                <span className="font-medium capitalize">
                                  {actualStatus}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Started</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.started_at && 
                            new Date(executions.find(e => e.id.toString() === selectedExecutionId)!.started_at).toLocaleString()
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Duration</div>
                        <div className="font-medium mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            if (!execution?.started_at) return 'N/A';
                            
                            const start = new Date(execution.started_at);
                            const end = execution.completed_at ? new Date(execution.completed_at) : new Date();
                            const duration = Math.round((end.getTime() - start.getTime()) / 1000);
                            
                            if (duration < 60) return `${duration}s`;
                            if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
                            return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Steps</div>
                        <div className="font-medium mt-1">
                          {executionSteps.length} total
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Execution Steps */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">Execution Steps</h3>
                    <div className="space-y-4">
                      {executionSteps.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No execution steps found</p>
                        </div>
                      ) : (
                        executionSteps.map((step, index) => {
                          const isCompleted = step.status === 'completed';
                          const isFailed = step.status === 'failed';
                          const isRunning = step.status === 'running';
                          
                          return (
                            <div key={step.id} className="relative">
                              {/* Connector Line */}
                              {index < executionSteps.length - 1 && (
                                <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200"></div>
                              )}
                              
                              <div className={`relative flex items-start gap-4 p-4 rounded-lg border ${
                                isFailed ? 'border-red-200 bg-red-50' :
                                isCompleted ? 'border-green-200 bg-green-50' :
                                isRunning ? 'border-blue-200 bg-blue-50' :
                                'border-gray-200 bg-gray-50'
                              } transition-all duration-300`}>
                                {/* Step Number & Icon */}
                                <div className="flex-shrink-0 flex flex-col items-center">
                                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-medium text-sm ${
                                    isFailed ? 'border-red-300 bg-red-100 text-red-800' :
                                    isCompleted ? 'border-green-300 bg-green-100 text-green-800' :
                                    isRunning ? 'border-blue-300 bg-blue-100 text-blue-800' :
                                    'border-gray-300 bg-gray-100 text-gray-800'
                                  }`}>
                                    {isFailed ? <XCircle className="h-4 w-4" /> :
                                     isCompleted ? <CheckCircle className="h-4 w-4" /> :
                                     isRunning ? <Clock className="h-4 w-4 animate-spin" /> :
                                     (index + 1)}
                                  </div>
                                </div>

                                {/* Step Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <h4 className={`font-medium ${
                                        isFailed ? 'text-red-800' :
                                        isCompleted ? 'text-green-800' :
                                        isRunning ? 'text-blue-800' :
                                        'text-gray-800'
                                      }`}>
                                        {step.action_type === 'email' && '📧 Send Email'}
                                        {step.action_type === 'sms' && '💬 Send SMS'}
                                        {step.action_type === 'delay' && '⏱️ Wait'}
                                        {step.action_type === 'create_crm_lead' && '🎯 Create CRM Lead'}
                                        {step.action_type === 'check' && '✅ Check'}
                                        {step.action_type === 'create_task' && '📋 Create Task'}
                                        {!['email', 'sms', 'delay', 'create_crm_lead', 'check', 'create_task'].includes(step.action_type) && 
                                          `${step.action_type.charAt(0).toUpperCase() + step.action_type.slice(1)} Action`
                                        }
                                      </h4>
                                      <div className="text-sm text-gray-500 space-y-1">
                                        {step.started_at && (
                                          <div>Started: {new Date(step.started_at).toLocaleString()}</div>
                                        )}
                                        {step.completed_at && (
                                          <div>Completed: {new Date(step.completed_at).toLocaleString()}</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      Step {index + 1}
                                    </div>
                                  </div>

                                  {/* Error Message */}
                                  {step.error_message && (
                                    <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                                      <strong>Error:</strong> {step.error_message}
                                    </div>
                                  )}

                                  {/* Result Data */}
                                  {step.result_data && Object.keys(step.result_data).length > 0 && (
                                    <details className="mt-2">
                                      <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                                        View Result Data
                                      </summary>
                                      <div className="mt-1 p-2 bg-white border border-gray-200 rounded text-xs overflow-auto max-h-32">
                                        <pre>{JSON.stringify(step.result_data, null, 2)}</pre>
                                      </div>
                                    </details>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trigger Settings Modal - Available for both desktop and mobile */}
      {isTriggerSettingsOpen && selectedTrigger && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4" style={{ marginTop: '80px', alignSelf: 'flex-start' }}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Trigger Settings</h2>
              <button
                onClick={handleCloseTriggerSettings}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              {selectedTrigger.type === 'booking' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Booking Event Type
                  </label>
                  <select
                    value={selectedTrigger.config.eventType}
                    onChange={(e) => {
                      const newEventType = e.target.value;
                      setTriggers(prev => prev.map(trigger => 
                        trigger.id === selectedTriggerId 
                          ? { ...trigger, config: { ...trigger.config, eventType: newEventType } }
                          : trigger
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="created">Created</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="canceled">Canceled</option>
                    <option value="deleted">Deleted</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              )}

              {selectedTrigger.type === 'form_submission' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Form Type
                    </label>
                    <select
                      value={selectedTrigger.config.formType || ''}
                      onChange={(e) => {
                        const newFormType = e.target.value;
                        setTriggers(prev => prev.map(trigger => 
                          trigger.id === selectedTriggerId 
                            ? { 
                                ...trigger, 
                                config: { 
                                  ...trigger.config, 
                                  formType: newFormType,
                                  formId: '' // Reset form ID when changing form type
                                } 
                              }
                            : trigger
                        ));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select form type...</option>
                      <option value="lead_form">Lead Form</option>
                      <option value="online_booking_banner">Online Booking Banner</option>
                    </select>
                  </div>

                  {selectedTrigger.config.formType === 'lead_form' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Form
                      </label>
                      <select
                        value={selectedTrigger.config.formId || ''}
                        onChange={(e) => {
                          const newFormId = e.target.value;
                          setTriggers(prev => prev.map(trigger => 
                            trigger.id === selectedTriggerId 
                              ? { ...trigger, config: { ...trigger.config, formId: newFormId } }
                              : trigger
                          ));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select lead form...</option>
                        {leadForms.map((form) => (
                          <option key={form.id} value={form.id}>
                            {form.name || form.title || 'Untitled Form'}
                          </option>
                        ))}
                      </select>
                      
                      {loadingLeadForms && (
                        <p className="text-xs text-gray-500 mt-1">
                          Loading lead forms...
                        </p>
                      )}
                      
                      {!loadingLeadForms && leadForms.length === 0 && (
                        <p className="text-xs text-yellow-600 mt-1">
                          No lead forms found. Create a lead form first to use this trigger.
                        </p>
                      )}
                    </div>
                  )}

                  {selectedTrigger.config.formType === 'online_booking_banner' && (
                    <div className="mb-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-blue-800">
                              Online Booking Banner Selected
                            </h4>
                            <p className="text-sm text-blue-700 mt-1">
                              This trigger will activate when customers submit the lead form in your online booking popup banner. 
                              Make sure you have enabled the popup banner in your Online Bookings settings.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={selectedTrigger.config.uiLabel || ''}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setTriggers(prev => prev.map(trigger => 
                      trigger.id === selectedTriggerId 
                        ? { ...trigger, config: { ...trigger.config, uiLabel: newUiLabel } }
                        : trigger
                    ));
                  }}
                  placeholder="e.g. 'New Customer SMS', 'Send Reminder', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this trigger on the canvas
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseTriggerSettings}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => selectedTriggerId && handleDeleteTrigger(selectedTriggerId)}
                  className="px-4 py-2 text-red-700 bg-red-100 hover:bg-red-200 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AutomationsV2; 